// ProposalPage.tsx
// ✅ Header matches example: Prepared (left) | Logo (center) | Meta (right)
// ✅ No overlay, no reordering, no broken blocks
// ✅ Print uses hidden iframe and preserves layout

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./ProposalPage.css";
// Loaded after ProposalPage.css on purpose: the CD redesign overrides the
// legacy proposal styles by cascade order, so it needs no !important.
import "./ProposalCD.css";
// Loaded LAST. Screen-only narrow-viewport layer for the customer /review
// route, which renders this component on phones. Every rule is inside
// `@media screen and (max-width: 768px)`, so the print output cannot see it.
import "./ProposalMobile.css";
import type { UserSettings, ProposalSection } from "./SettingsPage";
import { fetchSowTemplatesRows } from "./sowTemplates";
import {
  fetchProposalSections,
  saveProposalSectionEdit,
  type ProposalSectionEdit,
} from "./proposalSections";
import {
  insertSectionAfterVisible,
  mergeLayoutOrder,
  moveSectionVisible,
  orderedSections,
} from "./shared/sectionEditing";
import { uuidV4 } from "./utils/uid";
import { displayItemName, normalizeUnit } from "./shared/proposalSend";
import {
  filterSupersededSkirtingRows,
  formatStateCode,
} from "./shared/format";
import { supabase } from "./supabaseClient";
import type { ProposalVisual } from "./visualLibrary";
import {
  SPECIFIC_DATE_OPTION,
  defaultStartDate,
  formatStartDate,
  isStartDate,
  readStartDate,
  writeStartDate,
} from "./shared/timelineStartDate";
import { PERMIT_START_NOTE } from "./shared/permitStartNote";
import {
  proposalNotesStorageKey,
  legacyProposalNotesStorageKey,
  proposalToggleStorageKey,
  readProposalToggle,
} from "./shared/proposalSend";
import { dlog } from "./debugLog";
import {
  footerPageCss,
  footerSvgUri,
  footerText,
} from "./shared/proposalPrintFooter";
import InlineEditText from "./components/InlineEditText";
import {
  cleanDaysInput,
  cleanLabel,
  defaultNextStepsBody,
  normalizeProposalText,
  numberToWords,
  PROPOSAL_LABEL_DEFAULTS,
  resolveLabel,
  resolveNextStepsBody,
  resolveValidityDays,
  type ProposalLabelKey,
  type ProposalText,
} from "./shared/proposalText";


type AddItemRow = {
  rowId: string;
  qty?: number | null;
  unit?: string | null;
  lineBase?: number | null;

  picked?: { name: string; proposal_description?: string | null } | null;

  customName?: string | null;
  customDescription?: string | null;
  customQtyText?: string | null;

  isFixedPrice?: boolean;
  category?: string | null;
};

type ConstructionSowKey =
  | "new_construction"
  | "resurface"
  | "second_story"
  | "second_story_resurface"
  | "sleeper_system"
  | "second_story_sleeper";

function toSowKey(raw: string): ConstructionSowKey | "" {
  const v = (raw || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");

  if (v.includes("second") && v.includes("resurface"))
    return "second_story_resurface";
  if (v.includes("second") && v.includes("sleeper"))
    return "second_story_sleeper";
  if (v.includes("second")) return "second_story";
  if (v.includes("sleeper")) return "sleeper_system";
  if (v.includes("resurface") || v.includes("redeck")) return "resurface";
  if (v.includes("new")) return "new_construction";
  return "";
}

/**
 * Auto-generated Project Description (the one-liner above Scope of Work).
 * Built from the construction type + deck square footage. Matches the
 * construction type tolerantly (normalized, same precedence as toSowKey)
 * because DB values vary in spelling/casing. When the square footage is
 * missing/zero/blank, the "(approx N sf)" clause is dropped entirely so it
 * still reads cleanly.
 */
function buildProjectDescription(
  constructionType: string,
  sf: number | null | undefined
): string {
  const v = (constructionType || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");

  let base: string;
  if (v.includes("second") && v.includes("sleeper"))
    base = "Design and construction of a new rooftop deck";
  else if (v.includes("second") && v.includes("resurface"))
    base = "Resurfacing of an existing second-story deck";
  else if (v.includes("second"))
    base = "Design and construction of a new second-story deck";
  else if (v.includes("sleeper"))
    base = "Design and construction of a new ground-level deck";
  else if (v.includes("resurface") || v.includes("redeck"))
    base = "Resurfacing of an existing deck";
  else if (v.includes("new"))
    base = "Design and construction of a new custom deck";
  else base = "Custom deck project";

  const n = Number(sf);
  const sfPart =
    Number.isFinite(n) && n > 0 ? `, approximately ${n} sq ft` : "";
  return `${base}${sfPart}`;
}

// ------------------------------------------------------------
// Visual Appendix caption clamping (print).
//
// Chromium disables overflow clipping while paginating to PDF, so a CSS
// `-webkit-line-clamp` draws its ellipsis but STILL paints the overflowing
// lines outside the card. The only truncation that survives printing happens
// at layout time, so we shorten the caption text itself and render that copy
// for print. Screen keeps the full, unclamped caption.
//
// Geometry mirrors the print rules in ProposalPage.css: a 3.2in card, minus
// its 1px borders, minus the caption's 0.12in side padding.
// ------------------------------------------------------------
const PRINT_CAPTION_FONT_PX = 8 * (96 / 72); // 8pt
const PRINT_CAPTION_WIDTH_PX = 3.2 * 96 - 2 - 2 * 0.12 * 96;
const PRINT_CAPTION_MAX_LINES = 2;

/**
 * Canvas text measurer at the print caption's font, or null when unavailable.
 * `family` must be the caption's own computed font-family — the proposal body
 * uses a different stack, and measuring with it under-measures by ~6%, which is
 * enough to spill a third line onto the page.
 */
function makePrintCaptionMeasurer(
  family: string
): ((s: string) => number) | null {
  if (typeof document === "undefined" || !family) return null;
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return null;
  ctx.font = `${PRINT_CAPTION_FONT_PX}px ${family}`;
  return (s) => ctx.measureText(s).width;
}

/** Greedy word wrap, breaking inside a word only when it alone cannot fit. */
function wrapToLines(
  text: string,
  measure: (s: string) => number,
  width: number
): string[] {
  const lines: string[] = [];
  let cur = "";
  for (const word of text.split(" ")) {
    let w = word;
    // A single word wider than the column: hard-break it, matching the
    // `overflow-wrap: anywhere` the print stylesheet applies.
    while (measure(w) > width) {
      let cut = w.length - 1;
      while (cut > 1 && measure(w.slice(0, cut)) > width) cut--;
      if (cur) {
        lines.push(cur);
        cur = "";
      }
      lines.push(w.slice(0, cut));
      w = w.slice(cut);
    }
    const cand = cur ? `${cur} ${w}` : w;
    if (!cur || measure(cand) <= width) cur = cand;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

/** Shorten `text` so it occupies at most two printed lines, ellipsis included. */
export function clampCaptionForPrint(
  text: string,
  measure: (s: string) => number,
  width: number = PRINT_CAPTION_WIDTH_PX,
  maxLines: number = PRINT_CAPTION_MAX_LINES
): string {
  const clean = (text || "").trim().replace(/\s+/g, " ");
  if (!clean) return "";
  const lines = wrapToLines(clean, measure, width);
  if (lines.length <= maxLines) return clean;

  const head = lines.slice(0, maxLines - 1).join(" ");
  let tail = lines[maxLines - 1];
  while (tail && measure(`${tail}…`) > width) tail = tail.slice(0, -1);
  tail = tail.replace(/[\s,.;:!?—-]+$/, "");
  return `${head ? `${head} ` : ""}${tail}…`;
}

export type ProposalPageProps = {
  userSettings: UserSettings | null;
  estimateName: string;
  /** Stable per-estimate id. This — never the name — is what the Notes storage
   *  key is derived from, so two unnamed estimates can't share one key and leak
   *  notes into each other's proposal. */
  estimateId?: string | null;
  /** Called with the live Notes text whenever it changes, so the /review
   *  payload can send exactly what is on screen instead of re-reading the
   *  cache. Editor only — the read-only /review render never fires it. */
  onProposalNotesChange?: (notes: string) => void;
  /** True while App's startup cloud-draft load is in flight: the in-place
   *  editors stay view-only so nothing typed can be overwritten when the
   *  draft is applied. */
  editingLocked?: boolean;
  /** Org admin (org_members.role === 'admin'). Enables in-place editing of
   *  the shared proposal sections; without it they render exactly as
   *  printed, with no edit affordance at all. */
  canEditSections?: boolean;
  /** The shared section order changed (org_settings.proposal_layout_order);
   *  App persists it through the same settings save the Settings page uses. */
  onLayoutOrderChange?: (order: string[]) => void;
  /** The org-level proposal wording changed (labels, headings, Next steps,
   *  validity days — org_settings.proposal_text). App persists it through
   *  the same settings save the Settings page uses. Admin only. */
  onProposalTextChange?: (next: ProposalText) => void;
  /** Where that wording's save stands, for the on-screen note. */
  proposalTextSaveState?: "idle" | "saving" | "saved" | "error";
  /** True while a shared-section edit is unsaved or saving, so App's close
   *  guard (web beforeunload, desktop close dialog) can hold the window. */
  onSectionEditsPendingChange?: (pending: boolean) => void;
  finalEstimate: number;
  orgId?: string | null;

  constructionType: string;

  clientFirstName: string;
  clientLastName: string;
  clientTown: string;
  /** Two-letter state, printed beside the town in the identity header.
   *  Already carried in the /review payload (proposalSend), so the
   *  customer copy and the admin copy agree. */
  clientState?: string | null;
  clientEmail: string;

  deckingType?: string | null;
  railingType?: string | null;
  stairsType?: string | null;
  fastenerType?: string | null;
  demoType?: string | null;
  skirtingType?: string | null;
  /** The selected skirting CATEGORY ("Skirting" | "Lattice") — used only to
   *  print the correct DETAILS-table label. Internal value, not renamed. */
  skirtingCategory?: string | null;
  /** Matched product photos frozen into the snapshot at save time. Read here
   *  (not from the DB) so unauthenticated /review customers can render them. */
  visuals?: ProposalVisual[] | null;
  deckingDescription?: string | null;
  railingDescription?: string | null;
  stairsDescription?: string | null;
  fastenerDescription?: string | null;
  demoDescription?: string | null;
  skirtingDescription?: string | null;

  deckingSubtotal: number;
  railingSubtotal: number;
  stairsSubtotal: number;
  fastenerSubtotal: number;
  demoSubtotal: number;
  skirtingSubtotal: number;

  deckingQty?: number | null;
  deckingUnit?: string | null;
  railingQty?: number | null;
  railingUnit?: string | null;
  stairsQty?: number | null;
  stairsUnit?: string | null;
  fastenerQty?: number | null;
  fastenerUnit?: string | null;
  demoQty?: number | null;
  demoUnit?: string | null;
  skirtingQty?: number | null;
  skirtingUnit?: string | null;

  addItemsDetailed: AddItemRow[];

  /** Decking tiers the estimator ticked as "show on proposal", resolved to
   *  current totals. Amounts are recomputed upstream on every render (and
   *  frozen into the payload only at send time, like the line items), so
   *  nothing stale can print here.
   *  Absent / empty -> the Decking Choices block renders nothing. */
  deckingTierOptions?:
    | {
        level: string;
        total: number;
        /** The sidebar panel's own delta vs. the quoted total. */
        diff?: number | null;
        isCurrent?: boolean;
      }[]
    | null;
  /** The tier this estimate is quoting. Prints as the anchor row and in the
   *  intro line, and is independent of which tiers were ticked. */
  quotedDeckingTier?: string | null;

  upliftMultiplier: number;
  onSectionsSnapshot?: (sections: ProposalSection[]) => void;

  defaultShowLineItemPrices?: boolean;
  onEmailProposal?: () => void;
  readOnly?: boolean;
  proposalSectionsSnapshot?: ProposalSection[] | null;

  proposalId?: string | null;

  proposalNotesSnapshot?: string | null;
  // Per-estimate Scope of Work. Source of truth lives in App.tsx's snapshot
  // so it travels with the .est file and the cloud draft. Empty string → fall
  // back to the auto-generated template derived from current selections.
  sowText?: string;
  onSowTextChange?: (next: string) => void;
  // Persisted "user has edited this" flag. Once true, the textarea binds to the
  // raw stored value (no autoScopeText fallback) so an intentionally-empty or
  // custom SOW survives — untouched estimates still show the live template.
  sowTouched?: boolean;
  // Per-estimate Project Description (one-liner above the Scope of Work).
  // Same model as sowText: empty → auto-generated summary from
  // constructionType + deckingQty; first user edit wins from then on.
  projectDescription?: string;
  onProjectDescriptionChange?: (next: string) => void;
  projectDescriptionTouched?: boolean;
  startWeeksSnapshot?: number | null;
  durationDaysSnapshot?: number | null;
  startDateSnapshot?: string | null;
  /** Live estimate's Permit toggle (editor only). */
  includePermit?: boolean;
  /** Permit toggle frozen at send time; the read-only view uses this ONLY, so
   *  proposals sent before the permit note existed never pick it up. */
  permitStartNoteSnapshot?: boolean | null;
  showLineItemPricesSnapshot?: boolean | null;
  defaultShowVisualAppendix?: boolean;
  showVisualAppendixSnapshot?: boolean | null;
};
function formatStartWeeksRange(weeks: number) {
  if (weeks <= 1) return "1 week";
  if (weeks === 2) return "2 weeks";
  if (weeks === 3) return "3 weeks";
  if (weeks === 4) return "4 weeks";
  if (weeks <= 6) return "5–6 weeks";
  if (weeks <= 8) return "7–8 weeks";
  return `${weeks} weeks`;
}

function formatDurationRange(days: number) {
  if (days <= 2) return "1–2 days";
  if (days <= 5) return "3–5 days";
  if (days <= 6) return "4–6 days";
  if (days <= 10) return "7–10 days";
  return "10+ days";
}

function money0(n: number) {
  return Math.round(n || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
}

// Format a 10-digit US phone number as "(XXX) XXX-XXXX" for display.
// Strips non-digit characters so messy input still normalizes; if the
// digit count isn't 10, returns the raw input so partial/extension
// numbers aren't silently mangled.
function formatPhone(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

// Normalize legacy / system layout ids so ProposalPage matches SettingsPage
const normalizeLayoutId = (idRaw: any): string => {
  const id = String(idRaw || "").trim();
  if (id === "_details_") return "__details__";
  if (id === "_timeline_") return "__timeline__";
  return id;
};

function fmtQty(
  qty?: number | null,
  unit?: string | null,
  name?: string | null,
  category?: string | null
) {
  const q = typeof qty === "number" && isFinite(qty) ? qty : null;
  if (q == null || q <= 0) return "";

  // Spec §7: thousands-separated — "1,500 sf", not "1500 sf". Integers went
  // through String(q) and lost the separator.
  const qStr = q.toLocaleString(undefined, { maximumFractionDigits: 2 });

  // Spec §7: units lowercase, and long catalogue forms ("each", "LF")
  // collapsed to the canonical short form.
  const u0 = normalizeUnit(unit);
  if (!u0) return qStr;

  if (u0 === "multiplier") {
    const n = (name || "").toLowerCase();
    const c = (category || "").toLowerCase();

    if (n.includes(" sf")) return `${qStr} sf`;
    if (n.includes(" lf")) return `${qStr} lf`;
    if (n.includes(" ea")) return `${qStr} ea`;

    if (c.includes("deck") || c.includes("skirting") || c.includes("lattice"))
      return `${qStr} sf`;
    if (c.includes("rail")) return `${qStr} lf`;

    return qStr;
  }

  return `${qStr} ${u0}`;
}

/**
 * Show Cost / Show Visuals for the CURRENT estimate.
 *
 * The toggle carries the storage key (i.e. the estimate) it was read for.
 * ProposalPage never unmounts, and the toggles used to be read once at mount:
 * after switching from A to B the page showed — and PRINTED — A's setting
 * while Email sent B's stored one, so the paper proposal and the customer's
 * /review link could disagree about showing costs. A different key now reads
 * that estimate's own value. On /review (readOnly) the sent snapshot wins.
 * The setter keeps useState's updater shape; the click handlers persist the
 * new value under the current key themselves.
 */
function useEstimateToggle(
  name: "du_show_line_prices" | "du_show_visual_appendix",
  keyArgs: readonly [string | null, string],
  readOnly: boolean,
  snapshotValue: boolean | null | undefined
) {
  const key = proposalToggleStorageKey(name, ...keyArgs);
  const [entry, setEntry] = useState<{ key: string; on: boolean }>(() => ({
    key,
    on:
      readOnly && typeof snapshotValue === "boolean"
        ? snapshotValue
        : readProposalToggle(name, ...keyArgs),
  }));
  useEffect(() => {
    if (readOnly) return;
    setEntry((e) =>
      e.key === key ? e : { key, on: readProposalToggle(name, ...keyArgs) }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, readOnly]);
  const on =
    readOnly || entry.key === key ? entry.on : readProposalToggle(name, ...keyArgs);
  const setOn = (update: (v: boolean) => boolean) =>
    setEntry({ key, on: update(on) });
  return [on, setOn] as const;
}

/* Spec §7: the validity window (days) prints in three places — the terms
   line under the masthead, the Decking Choices fine print and the built-in
   Next steps paragraph. All three read validityDays below, which comes from
   the org setting (default 120, shared/proposalText.ts), so they can never
   disagree. A CUSTOM Next steps paragraph is the admin's own text and does
   not follow the number. */

/* Misc rows carry a free-text quantity the user typed ("23 LF"), which never
   passes through fmtQty. Lowercase only a recognised trailing unit token so
   the column reads consistently without mangling anything else. */
function normalizeQtyText(raw?: string | null): string {
  const t = (raw || "").toString().trim();
  if (!t) return "";
  return t.replace(
    /\b(SF|LF|EA|EACH|SQ ?FT|LIN(?:EAR)? ?FT)\b\s*$/i,
    (m) => normalizeUnit(m)
  );
}

function bulletLines(text?: string | null) {
  return (text || "")
    // Split on newlines AND on inline bullet characters. Settings stores
    // this as free text: some blocks arrive as one long line with "•"
    // separators rather than line breaks, which previously rendered as a
    // single run-on item with the bullets sitting inside the text.
    .split(/\r?\n|(?=[\u2022\u00b7])/)
    .map((s) => s.trim())
    // Strip a leading bullet the user typed (spec §7). These become <li>,
    // which draws its own marker — leaving it produced doubled bullets.
    .map((s) => s.replace(/^[\u2022\u00b7*\-\u2013\u2014]\s*/, "").trim())
    .filter(Boolean);
}

/* List vs paragraph is decided by the section's stored type (Format in the
   ⋯ menu), not guessed from its text — see the render below. */

/* ------------------------------------------------------------------
   MOBILE PRINT DETECTION (customer /review "Save PDF")

   The public review page runs in a plain, unauthenticated mobile
   browser — no Electron, no desktop assumptions. window.print() is the
   PDF mechanism, but iOS WebKit support is inconsistent: real iOS Safari
   shows a print/share sheet (works), while non-Safari iOS browsers
   (Chrome=CriOS, Firefox=FxiOS, Edge=EdgiOS) and in-app webviews opened
   from Gmail/Messages/Facebook frequently lack it or fail. These helpers
   let the Save PDF handler use native print where it works and fall back
   to platform-appropriate guidance everywhere else, instead of throwing
   an uncaught handler error.
   ------------------------------------------------------------------ */
function detectIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/\b(iPhone|iPad|iPod)\b/i.test(ua)) return true;
  // iPadOS 13+ reports a Mac UA; distinguish by touch support.
  return (
    ua.includes("Macintosh") &&
    typeof document !== "undefined" &&
    "ontouchend" in document
  );
}

// The proposal's 0.5in page margins come from an injected @page rule, which
// the print dialog ignores when its Margins setting is "None".
const PRINT_MARGINS_HINT =
  "For best results set Margins to Default in the print dialog.";

// Printed in place of an empty Scope of Work, and shown as the editor's
// placeholder so clicking it opens an empty field with the same wording.
/** Free text as printed paragraphs: a blank line starts a new paragraph
 *  (spaced by .du-tnPara, not by an empty printed line); a single line break
 *  inside a paragraph is kept via white-space:pre-wrap at the call site. Used
 *  by The project, Scope of work and Notes so all three print what was typed. */
function toParagraphs(text: string): string[] {
  return String(text || "")
    .split(/\n[ \t]*\n+/)
    .map((para) => para.trim())
    .filter(Boolean);
}

const SOW_FALLBACK_TEXT =
  "This project includes the construction of a custom outdoor deck designed to match the selected materials, layout, and site conditions. Final scope and details are based on the selections made during the estimate and are subject to on-site verification.";

// True only for genuine iOS Safari, where window.print() reliably works.
// Non-Safari iOS browsers carry a Cr/Fx/Edg/OPiOS token; in-app webviews
// typically lack the "Safari" token entirely.
function isIOSSafari(): boolean {
  if (!detectIOS()) return false;
  const ua = navigator.userAgent || "";
  const isOtherIOSBrowser = /\b(CriOS|FxiOS|EdgiOS|OPiOS|GSA|mercury)\b/i.test(
    ua
  );
  return /\bSafari\b/i.test(ua) && !isOtherIOSBrowser;
}

export default function ProposalPage(props: ProposalPageProps) {
    // ============================
  // ============================
  // 🔒 Freeze proposal rendering (no live updates)
  // ============================
  const latestPropsRef = useRef<ProposalPageProps>(props);
  latestPropsRef.current = props;

  const [proposalSnapshot, setProposalSnapshot] = useState<ProposalPageProps>(() => props);

  // 📬 Email tracking (opened / clicked)
  const [emailOpenedAt, setEmailOpenedAt] = useState<string | null>(null);
  const [emailClickedAt, setEmailClickedAt] = useState<string | null>(null);

  const loadEmailTracking = async () => {
    const pid = (props.proposalId || "").trim();
    if (!pid) return;
    try {
      const { data, error } = await supabase
        .from("proposal_tracking_events")
        .select("event_type, created_at")
        .eq("proposal_id", pid)
        .order("created_at", { ascending: false })
        .limit(200);

      if (error) throw error;

      const events = Array.isArray(data) ? data : [];
      const openedEvents = events.filter((e: any) => e.event_type === "open");
      const clickedEvents = events.filter((e: any) => e.event_type === "click");

      setEmailOpenedAt(openedEvents[0]?.created_at || null);
      setEmailClickedAt(clickedEvents[0]?.created_at || null);
    } catch {
      // silent fail
    }
  };

  // 🔔 Track if proposal is out-of-date
  const [needsRefresh, setNeedsRefresh] = useState(false);
const liveUserSettings = props.userSettings;
  // Re-fetch sections + re-render proposal ONLY when user clicks refresh
  const [proposalRefreshKey, setProposalRefreshKey] = useState(0);
const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(() => new Date());
  const refreshProposal = () => {
    setProposalSnapshot(latestPropsRef.current);
    setProposalRefreshKey((k) => k + 1);
    setNeedsRefresh(false);
    setLastRefreshedAt(new Date());
  };

  // ✅ Auto-refresh when switching to a different estimate (fresh context).
  // Keyed on the estimate ID as well as the name: keyed on the name alone,
  // switching between two estimates with the SAME name (two unnamed ones —
  // File > New from an unnamed estimate — or two files that share a name)
  // left the previous estimate's frozen document up, e.g. B's client with A's
  // price, and Print printed that mix. The name stays in the key so a rename
  // (and the auto-name as client details are typed) still refreshes as before.
  useEffect(() => {
    setProposalSnapshot(latestPropsRef.current);
    setProposalRefreshKey((k) => k + 1);
    setNeedsRefresh(false);
  }, [props.estimateId, props.estimateName]);

  // ✅ Load email tracking for this proposal (auto-refresh)
  //
  // ADMIN ONLY. The opened/clicked badges are our own send telemetry, and
  // ReviewProposalPage renders THIS component with readOnly for the public
  // /review route. Ungated, every customer sitting on a proposal link polled
  // proposal_tracking_events anonymously every 15s for as long as the tab
  // stayed open — showing our send history to the customer and generating
  // roughly 15k requests a day. It is also the anon read path that the
  // tracking/pricing lockdown migration removes, so this gate is that
  // migration's prerequisite.
  //
  // readOnly is read off props rather than the destructured snapshot below:
  // that destructuring happens later in the body, so naming it here would hit
  // the temporal dead zone when React evaluates the dependency array.
  //
  // Also pauses while the tab is hidden — a backgrounded proposal polled just
  // as hard as a focused one.
  useEffect(() => {
    if (props.readOnly) return;
    if (!props.proposalId) {
      setEmailOpenedAt(null);
      setEmailClickedAt(null);
      return;
    }

    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      if (timer !== null) return;
      loadEmailTracking();
      timer = setInterval(loadEmailTracking, 15000);
    };
    const onVisibility = () => {
      if (typeof document !== "undefined" && document.hidden) stop();
      else start();
    };

    if (typeof document === "undefined" || !document.hidden) start();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.proposalId, props.readOnly]);
  // ✅ Build a lightweight signature of the proposal inputs (so we can detect stale snapshot)
  const liveSig = useMemo(() => {
    return JSON.stringify({
      finalEstimate: props.finalEstimate,
      deckingType: props.deckingType,
      railingType: props.railingType,
      stairsType: props.stairsType,
      fastenerType: props.fastenerType,
      demoType: props.demoType,
      skirtingType: props.skirtingType,
      addItemsCount: (props.addItemsDetailed || []).length,
    });
  }, [
    props.finalEstimate,
    props.deckingType,
    props.railingType,
    props.stairsType,
    props.fastenerType,
    props.demoType,
    props.skirtingType,
    props.addItemsDetailed,
  ]);

  const snapSig = useMemo(() => {
    return JSON.stringify({
      finalEstimate: proposalSnapshot.finalEstimate,
      deckingType: proposalSnapshot.deckingType,
      railingType: proposalSnapshot.railingType,
      stairsType: proposalSnapshot.stairsType,
      fastenerType: proposalSnapshot.fastenerType,
      demoType: proposalSnapshot.demoType,
      skirtingType: proposalSnapshot.skirtingType,
      addItemsCount: (proposalSnapshot.addItemsDetailed || []).length,
    });
  }, [
    proposalSnapshot.finalEstimate,
    proposalSnapshot.deckingType,
    proposalSnapshot.railingType,
    proposalSnapshot.stairsType,
    proposalSnapshot.fastenerType,
    proposalSnapshot.demoType,
    proposalSnapshot.skirtingType,
    proposalSnapshot.addItemsDetailed,
  ]);
    useEffect(() => {
    setNeedsRefresh(liveSig !== snapSig);
  }, [liveSig, snapSig]);

  
  const {
    orgId,
    userSettings,
    estimateName,
    // NOTE: estimateId is deliberately NOT destructured from the frozen
    // snapshot. Identity must come from the live props (see NOTES_KEY below) —
    // the snapshot re-syncs only on a name change, so a snapshot-derived id
    // points at the previous estimate across New Project.
    finalEstimate,
    constructionType,


    deckingType,
    railingType,
    stairsType,
    fastenerType,
    demoType,
    skirtingType,
    skirtingCategory,
    deckingDescription,
    railingDescription,
    stairsDescription,
    fastenerDescription,
    demoDescription,
    skirtingDescription,

    deckingSubtotal,
    railingSubtotal,
    stairsSubtotal,
    fastenerSubtotal,
    demoSubtotal,
    skirtingSubtotal,

    deckingQty,
    deckingUnit,
    railingQty,
    railingUnit,
    stairsQty,
    stairsUnit,
    fastenerQty,
    fastenerUnit,
    demoQty,
    demoUnit,
    skirtingQty,
    skirtingUnit,

    addItemsDetailed,
    deckingTierOptions,
    quotedDeckingTier,
    upliftMultiplier,
    defaultShowLineItemPrices,
    readOnly,

    proposalNotesSnapshot,
    startWeeksSnapshot,
    durationDaysSnapshot,
    startDateSnapshot,
    permitStartNoteSnapshot,
    showLineItemPricesSnapshot,
    defaultShowVisualAppendix,
    showVisualAppendixSnapshot,
    proposalSectionsSnapshot,
    

} = proposalSnapshot;

  /* Client identity is read from LIVE props, never the frozen snapshot —
     the same exception estimateId already makes above.

     The freeze exists so PRICING cannot shift while a proposal is being
     reviewed or sent. Customer identity is not pricing, and the staleness
     signature (liveSig/snapSig) tracks only finalEstimate + the material
     types + add-on count — no client fields whatsoever. So editing a name,
     town, state or email in the Estimator did not mark the proposal stale
     and the snapshot never re-synced: the proposal kept printing whatever
     the customer details were at first render. That is how the newly added
     state came out blank, and it applied equally to town/email/name before.

     Safe for /review: there, props come from the saved payload and never
     change, so live and snapshot are identical. */
  const {
    clientFirstName,
    clientLastName,
    clientTown,
    clientState,
    clientEmail,
  } = props;
  const docRef = useRef<HTMLElement | null>(null);

  // Printed footer on every sheet (company name + address · phone · website,
  // "Page X of Y"), drawn in the @page bottom margin so page breaks don't
  // move. See src/shared/proposalPrintFooter.ts. On /review userSettings is
  // the copy frozen at send time, so the footer matches what was sent.
  const footerUri = useMemo(
    () => footerSvgUri(footerText(userSettings as any, formatPhone)),
    [userSettings]
  );
  const footerCssRef = useRef("");
  footerCssRef.current = footerPageCss(footerUri);

  // Customer "Save PDF" guidance (shown only when native print is
  // unavailable/failed on this device — see handleSavePdf below).
  const [printHint, setPrintHint] = useState<string | null>(null);

  // Proposal-only page box: letter, 0.5in on every side — a real bottom margin
  // on every sheet, which office printers need. @page can't be scoped with a
  // selector and this component stays mounted (display:none) behind the other
  // tabs, so injecting on mount would leak into Contract / ST-124 printing.
  // Instead, like ContractPage's printContract, inject right before printing —
  // and only if the proposal is the screen actually being printed — then
  // remove it on afterprint. beforeprint also catches Ctrl+P / the browser
  // menu, not just the Print button. Appended last, it outranks the static
  // @page rules in the bundle. The print dialog's Margins must be "Default"
  // for it to apply (hence the hint by the print buttons).
  useEffect(() => {
    const STYLE_ID = "du-proposal-print-style";
    const onBeforePrint = () => {
      document.getElementById(STYLE_ID)?.remove();
      const doc = docRef.current;
      if (!doc || doc.offsetParent === null) return; // hidden tab
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.setAttribute("media", "print");
      style.textContent = `@page { size: letter; margin: 0.5in;${footerCssRef.current} }`;
      document.head.appendChild(style);
    };
    const onAfterPrint = () => {
      document.getElementById(STYLE_ID)?.remove();
    };
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
      onAfterPrint();
    };
  }, []);

  // ------------------------------------------------------------
  // WATERMARK ART — greyscaled in the PIXELS, not with a CSS filter.
  //
  // The page-2 watermark carried `filter: grayscale(1)` in CSS. That computes
  // correctly in both screen and print media (verified against production), so
  // the /review print path renders it fully neutral. But a CSS filter is a
  // RENDER-TIME instruction, and print engines are free to drop it: the
  // element also sits at `z-index: -1` inside an `isolation: isolate` context
  // and carries `print-color-adjust: exact`, and some engines composite that
  // negative layer separately — painting the ORIGINAL image and losing the
  // filter. That is the reported "prints in colour", which never reproduced in
  // Chromium here.
  //
  // Baking the luminance into the bitmap removes the dependency entirely:
  // there is no filter left to drop, so every engine, webview and print
  // pipeline gets grey pixels. Rec. 709 luma, matching what CSS grayscale()
  // computes, so the appearance is unchanged where the filter DID work.
  //
  // Alpha is left untouched so a transparent logo stays transparent. The CSS
  // filter is deliberately kept as a belt-and-braces fallback — it is
  // idempotent on already-grey pixels, and still covers the fallback paths
  // below where the original colour source is used.
  // ------------------------------------------------------------
  const logoDataUrl = userSettings?.logoDataUrl || "";
  const [watermarkSrc, setWatermarkSrc] = useState<string>("");

  useEffect(() => {
    if (!logoDataUrl) {
      setWatermarkSrc("");
      return;
    }

    let alive = true;
    // Fall back to the colour original (still CSS-filtered) rather than
    // dropping the watermark, in every failure path below.
    const fallback = () => {
      if (alive) setWatermarkSrc(logoDataUrl);
    };

    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return fallback();

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return fallback();

        ctx.drawImage(img, 0, 0);
        const frame = ctx.getImageData(0, 0, w, h);
        const px = frame.data;
        for (let i = 0; i < px.length; i += 4) {
          const luma = 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
          px[i] = px[i + 1] = px[i + 2] = luma;
        }
        ctx.putImageData(frame, 0, 0);

        if (alive) setWatermarkSrc(canvas.toDataURL("image/png"));
      } catch {
        // Canvas unavailable or the export threw — never block the proposal.
        fallback();
      }
    };
    img.onerror = fallback;
    img.src = logoDataUrl;

    return () => {
      alive = false;
    };
  }, [logoDataUrl]);

  // Safe "Save PDF" for the unauthenticated customer /review view.
  // Feature-detects window.print() and runs it in try/catch so a failure
  // can never throw an uncaught handler error (which surfaced as the
  // "Script error" crash on iOS). On iOS browsers other than Safari —
  // where print is unreliable or silently does nothing — we skip the call
  // and show Share/open-in-Safari guidance instead.
  const IOS_OTHER_HINT =
    "To save as PDF: tap your browser's Share button, then Print — or open this link in Safari for a one-tap save.";
  const GENERIC_HINT =
    "To save as PDF, use your browser's Print option (often under the Share or menu button).";
  const handleSavePdf = () => {
    setPrintHint(null);

    // iOS but not Safari (Chrome/Firefox/Edge on iOS, or an in-app webview):
    // print() is unreliable here, so guide the customer instead of attempting
    // a call that may throw or silently do nothing.
    if (detectIOS() && !isIOSSafari()) {
      setPrintHint(IOS_OTHER_HINT);
      return;
    }

    if (typeof window !== "undefined" && typeof window.print === "function") {
      try {
        window.print();
        return;
      } catch {
        // fall through to guidance
      }
    }

    setPrintHint(detectIOS() ? IOS_OTHER_HINT : GENERIC_HINT);
  };

  const safeOrgId = orgId ?? null;

// 🔔 Detect when estimator changes (mark proposal stale)
useEffect(() => {
  if (props.finalEstimate !== proposalSnapshot.finalEstimate) {
    setNeedsRefresh(true);
  }
}, [props.finalEstimate, proposalSnapshot.finalEstimate]);
  const [dbProposalSections, setDbProposalSections] = useState<
    ProposalSection[]
  >([]);

 useEffect(() => {
  let alive = true;

  (async () => {
    if (readOnly) {
      setDbProposalSections([]);
      return;
    }

    if (!safeOrgId) {
      setDbProposalSections([]);
      return;
    }

    try {
      const rows = await fetchProposalSections(safeOrgId);
      if (!alive) return;
      // Never replace a shared-section edit that has not been saved yet.
      if (dirtySectionIdsRef.current.length > 0) return;
      const next = Array.isArray(rows) ? rows : [];
      setDbProposalSections(next);
      props.onSectionsSnapshot?.(next);
    } catch {
      if (!alive) return;
      setDbProposalSections([]);
    }
  })();

  return () => {
    alive = false;
  };
}, [safeOrgId, readOnly, proposalRefreshKey]);

  // ------------------------------------------------------------
  // In-place editing of the SHARED sections (org admins only).
  // Edits are local while an editor is open; closing it saves through
  // saveProposalSectionEdit — a fresh read of the server rows, then the same
  // upsertProposalSections / deleteProposalSection writes Settings makes.
  // Saves run one at a time, in order, so a move or add queued behind a text
  // edit always builds on the saved text.
  // ------------------------------------------------------------
  const sectionsRef = useRef<ProposalSection[]>(dbProposalSections);
  sectionsRef.current = dbProposalSections;
  // Current org layout order; assigned where layoutOrder is computed below.
  const layoutOrderRef = useRef<string[]>([]);
  const [dirtySectionIds, setDirtySectionIds] = useState<string[]>([]);
  const dirtySectionIdsRef = useRef<string[]>([]);
  dirtySectionIdsRef.current = dirtySectionIds;
  const [sectionSaveState, setSectionSaveState] = useState<{
    kind: "idle" | "saving" | "saved" | "error";
    message?: string;
  }>({ kind: "idle" });
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  // Per-row "⋯" menu (move up / move down / remove). Opened by click or tap,
  // closed by clicking elsewhere or Esc — never by hover, so it is reachable
  // with a mouse and on a touch screen alike.
  const [sectionMenuId, setSectionMenuId] = useState<string | null>(null);
  useEffect(() => {
    if (!sectionMenuId) return;
    const onDown = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest && t.closest(".du-sectionTools.is-open")) return;
      setSectionMenuId(null);
      setConfirmRemoveId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSectionMenuId(null);
        setConfirmRemoveId(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sectionMenuId]);
  // A just-added section opens its heading editor as soon as its row mounts,
  // so it is focused — and its controls visible — straight away.
  const [autoOpenSectionId, setAutoOpenSectionId] = useState<string | null>(
    null
  );
  const sectionSaveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const sectionSavesInFlightRef = useRef(0);

  const sectionEditsPending =
    dirtySectionIds.length > 0 || sectionSaveState.kind === "saving";
  const onSectionEditsPendingChange = props.onSectionEditsPendingChange;
  useEffect(() => {
    onSectionEditsPendingChange?.(sectionEditsPending);
  }, [sectionEditsPending, onSectionEditsPendingChange]);

  const runSectionSave = (edit: ProposalSectionEdit): Promise<boolean> => {
    const orgIdNow = safeOrgId || "";
    const job = sectionSaveQueueRef.current.then(async () => {
      sectionSavesInFlightRef.current += 1;
      setSectionSaveState({ kind: "saving" });
      try {
        const res = await saveProposalSectionEdit(
          orgIdNow,
          layoutOrderRef.current,
          edit
        );
        setDbProposalSections(res.sections);
        props.onSectionsSnapshot?.(res.sections);
        props.onLayoutOrderChange?.(res.order);
        setSectionSaveState({ kind: "saved" });
        return true;
      } catch (e: any) {
        setSectionSaveState({
          kind: "error",
          message: String(e?.message || e || "Save failed."),
        });
        return false;
      } finally {
        sectionSavesInFlightRef.current -= 1;
      }
    });
    sectionSaveQueueRef.current = job.catch(() => false);
    return job;
  };

  /** Resolves once every queued shared-section save has finished. The send
   *  path re-reads the sections from the database, so it must wait. */
  const flushSectionSaves = () => sectionSaveQueueRef.current;

  const editSectionLocal = (id: string, patch: Partial<ProposalSection>) => {
    setDbProposalSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );
    setDirtySectionIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  /** Editor closed: save this section's title/text onto the server's rows. */
  const commitSection = (id: string) => {
    if (!dirtySectionIdsRef.current.includes(id)) return;
    const local = sectionsRef.current.find((s) => s.id === id);
    if (!local) return;
    void runSectionSave((fresh, order) => ({
      sections: fresh.some((s) => s.id === id)
        ? fresh.map((s) =>
            s.id === id ? { ...s, title: local.title, text: local.text } : s
          )
        : [...fresh, local],
      order: order.includes(id) ? order : [...order, id],
    })).then((ok) => {
      if (ok) setDirtySectionIds((prev) => prev.filter((x) => x !== id));
    });
  };

  /** Format: Paragraph / Bullets. Changes only the stored type — the text is
   *  never touched, so switching back and forth is lossless. */
  const setSectionFormat = (id: string, type: "paragraph" | "bullets") => {
    void runSectionSave((fresh, order) => ({
      sections: fresh.map((s) => (s.id === id ? { ...s, type } : s)),
      order,
    }));
  };

  /** Show on proposals / Hide from proposals. A hidden section keeps its
   *  place and its text; it just never prints or reaches /review. */
  const setSectionEnabled = (id: string, enabled: boolean) => {
    void runSectionSave((fresh, order) => ({
      sections: fresh.map((s) => (s.id === id ? { ...s, enabled } : s)),
      order,
    }));
  };

  const moveSection = (id: string, dir: -1 | 1) => {
    void runSectionSave((fresh, order) => ({
      sections: fresh,
      // The editor shows hidden sections too, so step past those as well.
      order: moveSectionVisible(order, fresh, id, dir, () => true),
    }));
  };

  const addSection = () => {
    const created: ProposalSection = {
      id: uuidV4(),
      title: "New section",
      enabled: true,
      type: "paragraph",
      text: "",
    };
    // Set before the save so the row mounts already open.
    setAutoOpenSectionId(created.id);
    void runSectionSave((fresh, order) => {
      const sections = [...fresh, created];
      return {
        sections,
        order: insertSectionAfterVisible(
          order,
          sections,
          created.id,
          () => true
        ),
      };
    }).then((ok) => {
      if (!ok) setAutoOpenSectionId(null);
    });
  };

  const removeSection = (id: string) => {
    setConfirmRemoveId(null);
    setSectionMenuId(null);
    setDirtySectionIds((prev) => prev.filter((x) => x !== id));
    void runSectionSave((fresh, order) => ({
      sections: fresh.filter((s) => s.id !== id),
      order: order.filter((x) => x !== id),
      deleteId: fresh.some((s) => s.id === id) ? id : undefined,
    }));
  };

  // Storage key for the two proposal toggles. Shared helper, so the editor
  // seeds from exactly the key proposalSend reads at send time. LIVE props,
  // like that send path — never the frozen snapshot's name.
  const toggleKeyArgs = [
    props.estimateId ?? null,
    props.estimateName || "",
  ] as const;

  const [showLineItemPrices, setShowLineItemPrices] = useEstimateToggle(
    "du_show_line_prices",
    toggleKeyArgs,
    !!readOnly,
    showLineItemPricesSnapshot
  );

  // Show/hide the Visual Appendix. Mirrors the showLineItemPrices toggle:
  // on /review (readOnly) it honors the frozen snapshot value; in the editor
  // it seeds from localStorage — the same value proposalSend freezes into
  // showVisualAppendixSnapshot at send time.
  //
  // These used to seed from `defaultShow*`, which NO caller has ever passed
  // (App.tsx and MobileApp both omit it), so both toggles silently resolved to
  // `!!undefined` → false on every mount. The button therefore reset to OFF on
  // each reload while localStorage kept the real value, and the send path read
  // localStorage — so a proposal could go out with visuals ON while the UI
  // showed "Show Visuals". The default props are kept as the last-resort
  // fallback inside readProposalToggle's absent case (false), which preserves
  // the previous "hidden unless explicitly enabled" behaviour.
  const [showVisualAppendix, setShowVisualAppendix] = useEstimateToggle(
    "du_show_visual_appendix",
    toggleKeyArgs,
    !!readOnly,
    showVisualAppendixSnapshot
  );

  // Timeline
  //
  // This page never unmounts (App only hides it), so the values must follow
  // the CURRENT estimate. They used to be read once at mount and written back
  // whenever the name changed — so opening estimate B after A showed A's
  // Timeline and saved it over B's real value, silently.
  //
  // `owner` says which estimate the values in state belong to. The estimate
  // ID tells a switch (new id: load that estimate's own values) from a rename
  // (same id: keep the values, save them under the new name — the storage key
  // is still the name until the Notes/Timeline id-keying migration). Values
  // are only ever written for the estimate they belong to.
  const timelineName = props.estimateName || "";
  const timelineOwner = (props.estimateId || "").trim() || `name:${timelineName}`;
  const timelineOwnerRef = useRef(timelineOwner);
  timelineOwnerRef.current = timelineOwner;
  const timelineNameRef = useRef(timelineName);
  timelineNameRef.current = timelineName;
  // startDate "" = print the week range; a YYYY-MM-DD date prints that date.
  type TimelineState = { owner: string; start: number; dur: number; startDate: string };
  const readTimeline = (owner: string, name: string): TimelineState => {
    const num = (key: string, fallback: number) => {
      try {
        const n = Number(localStorage.getItem(key));
        return Number.isFinite(n) && n >= 1 ? n : fallback;
      } catch {
        return fallback;
      }
    };
    return {
      owner,
      start: num(`du_timeline_start_weeks::${name}`, 3),
      dur: num(`du_timeline_duration_days::${name}`, 2),
      startDate: readStartDate(name),
    };
  };
  const [timelineState, setTimelineState] = useState<TimelineState>(() => {
    if (readOnly) {
      return {
        owner: timelineOwner,
        start: Number.isFinite(Number(startWeeksSnapshot))
          ? Number(startWeeksSnapshot)
          : readTimeline(timelineOwner, estimateName).start,
        dur: Number.isFinite(Number(durationDaysSnapshot))
          ? Number(durationDaysSnapshot)
          : readTimeline(timelineOwner, estimateName).dur,
        // Snapshot only: an older proposal sent without a date stays on weeks.
        startDate: isStartDate(startDateSnapshot) ? startDateSnapshot : "",
      };
    }
    return readTimeline(timelineOwner, timelineName);
  });
  // Another estimate is current: show ITS values from this render on, never
  // the previous estimate's.
  const timeline =
    readOnly || timelineState.owner === timelineOwner
      ? timelineState
      : readTimeline(timelineOwner, timelineName);
  useEffect(() => {
    if (readOnly) return;
    setTimelineState((t) =>
      t.owner === timelineOwner ? t : readTimeline(timelineOwner, timelineName)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineOwner, readOnly]);
  const startWeeks = timeline.start;
  const durationDays = timeline.dur;
  const startDate = timeline.startDate;
  const updateTimeline = (
    patch: Partial<Pick<TimelineState, "start" | "dur" | "startDate">>
  ) =>
    setTimelineState((t) => {
      const base =
        t.owner === timelineOwnerRef.current
          ? t
          : readTimeline(timelineOwnerRef.current, timelineNameRef.current);
      return { ...base, ...patch };
    });
  const setStartWeeks = (v: number) => updateTimeline({ start: v, startDate: "" });
  const setStartDate = (v: string) => updateTimeline({ startDate: v });
  const setDurationDays = (v: number) => updateTimeline({ dur: v });

  useEffect(() => {
    if (readOnly) return;
    // Only the current estimate's own values are saved — never a previous
    // estimate's values under this estimate's name.
    if (timelineState.owner !== timelineOwner) return;
    try {
      localStorage.setItem(
        `du_timeline_start_weeks::${timelineName}`,
        String(timelineState.start)
      );
      localStorage.setItem(
        `du_timeline_duration_days::${timelineName}`,
        String(timelineState.dur)
      );
      writeStartDate(timelineName, timelineState.startDate);
    } catch {}
  }, [timelineState, timelineOwner, timelineName, readOnly]);

  // Notes
  //
  // Keyed on the estimate's STABLE ID, never its name. A name is blank on every
  // new estimate, so a name-derived key collapses to the degenerate
  // "du_proposal_notes::" that all unnamed estimates share — type a note, start
  // a new estimate, and the note follows you into the customer's proposal.
  //
  // The id comes from the LIVE props, not from proposalSnapshot. The snapshot
  // is deliberately frozen (it re-syncs only when estimateName changes, so the
  // rendered proposal doesn't move under the user), but New Project mints a new
  // id WITHOUT changing the name — "" both before and after — so a
  // snapshot-derived key kept pointing at the previous estimate, or at the ""
  // the id passes through mid-reset. Identity must track the live estimate even
  // while the rendered figures stay frozen.
  //
  // proposalNotesStorageKey / resolveProposalNotes are the same helpers
  // buildProposalPayload reads through, so the screen, the print/PDF output and
  // the customer /review row cannot drift apart.
  const NOTES_KEY = proposalNotesStorageKey((props.estimateId || "").trim());
  // Notes written by a pre-id build still live under the name key. Read through
  // to it (never write it), and only for a NON-BLANK name — the blank-name key
  // is exactly the cross-estimate leak this keying exists to prevent. The write
  // effect then migrates the value onto the id key.
  const LEGACY_NOTES_KEY = legacyProposalNotesStorageKey(props.estimateName || "");

  // Notes are stored WITH the key they belong to. Effects run in declaration
  // order, so on the commit where NOTES_KEY changes the write effect used to
  // fire while the state still held the PREVIOUS estimate's text and stamp it
  // under the NEW estimate's key — the leak, written to disk, before the read
  // effect's reset landed. Pairing the text with its key makes that
  // unrepresentable: a value whose key doesn't match the current estimate is
  // neither rendered nor persisted.
  const [notesEntry, setNotesEntry] = useState<{ key: string; text: string }>(
    () => ({ key: NOTES_KEY, text: "" })
  );
  // "+ Add notes" was clicked: show the (still empty) Notes column with its
  // editor open. Cleared when that editor closes.
  const [notesAdding, setNotesAdding] = useState(false);

  const proposalNotes = readOnly
    ? String(proposalNotesSnapshot ?? "")
    : notesEntry.key === NOTES_KEY
      ? notesEntry.text
      : "";

  /** The note as paragraphs. Authors separate paragraphs with a blank line,
   *  and the whole note used to render through white-space:pre-wrap on one
   *  element, so that blank line printed as a full empty line — the only
   *  place on the sheet where paragraphs were spaced that way. Splitting on
   *  runs of blank lines lets CSS space them like every other paragraph
   *  (.du-tnPara). Single newlines are left alone: pre-wrap still breaks
   *  them, so a typed list inside one paragraph survives. */
  // Live props, not the frozen proposalSnapshot: that only re-syncs on a name
  // change, so flipping Permit after opening the proposal never showed up.
  const showPermitNote = readOnly
    ? !!permitStartNoteSnapshot
    : !!props.includePermit;

  const notesParagraphs = React.useMemo(
    () => toParagraphs(proposalNotes),
    [proposalNotes]
  );

  useEffect(() => {
    if (readOnly) return;
    if (!NOTES_KEY) {
      setNotesEntry({ key: "", text: "" });
      return;
    }
    try {
      const saved =
        localStorage.getItem(NOTES_KEY) ||
        (LEGACY_NOTES_KEY ? localStorage.getItem(LEGACY_NOTES_KEY) : "") ||
        "";
      setNotesEntry({ key: NOTES_KEY, text: saved });
    } catch {
      setNotesEntry({ key: NOTES_KEY, text: "" });
    }
  }, [NOTES_KEY, LEGACY_NOTES_KEY, readOnly]);

  useEffect(() => {
    if (readOnly) return;
    if (!NOTES_KEY) return;
    // Only the current estimate's own notes may be persisted.
    if (notesEntry.key !== NOTES_KEY) return;
    try {
      localStorage.setItem(NOTES_KEY, notesEntry.text || "");

      // Keep an EXISTING legacy name entry in sync — never create one.
      //
      // Estimate files saved before the id existed carry no estimateId, so
      // App.applySnapshot mints a fresh one on every open. Their notes are
      // found through the legacy name key, re-keyed onto that day's id — and
      // if we only wrote the id key, the next open would mint a different id,
      // fall back to the legacy key again, and show the pre-edit text: the
      // user's edit silently lost. Mirroring the edit back keeps those files
      // editable until they're re-saved with a stable id.
      //
      // Only mirrors a key that already holds a value, so new estimates stay
      // id-only and no fresh name-keyed entries are ever manufactured.
      if (
        LEGACY_NOTES_KEY &&
        LEGACY_NOTES_KEY !== NOTES_KEY &&
        localStorage.getItem(LEGACY_NOTES_KEY) !== null
      ) {
        localStorage.setItem(LEGACY_NOTES_KEY, notesEntry.text || "");
      }
    } catch {}
  }, [NOTES_KEY, LEGACY_NOTES_KEY, notesEntry, readOnly]);

  // Hand the live value up so the /review payload sends what is on screen
  // rather than re-reading the cache and hoping the two agree.
  const onProposalNotesChange = props.onProposalNotesChange;
  useEffect(() => {
    if (readOnly) return;
    onProposalNotesChange?.(proposalNotes);
  }, [proposalNotes, readOnly, onProposalNotesChange]);

  const lineTotal = (baseCost: number, isFixedPrice?: boolean) =>
    (baseCost || 0) * (isFixedPrice ? 1 : upliftMultiplier || 1);

  // SOW
  const sowKey = useMemo(() => toSowKey(constructionType), [constructionType]);

  const [sowTemplatesMap, setSowTemplatesMap] = useState<
    Record<string, string>
  >(() => {
    try {
      return JSON.parse(localStorage.getItem("du_sow_templates_v1") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchSowTemplatesRows();
        const map: Record<string, string> = {};
        rows.forEach((r) => {
          map[r.construction_key] = r.body || "";
        });
        setSowTemplatesMap(map);
      } catch {
        setSowTemplatesMap({});
      }
    })();
  }, []);

  // The in-app editor, as opposed to the customer's read-only /review page.
  // Any signed-in user gets this — it is NOT an admin check (it used to be
  // named isAdmin, which it never was).
  const isEditorView = !readOnly;
  // The Project / Scope of work / Notes are edited in place by ANY user —
  // they belong to this one proposal. View-only while the startup cloud draft
  // is loading (editingLocked): App.applySnapshot replaces these fields when
  // it lands, which would wipe anything typed.
  const inlineEditable = isEditorView && !props.editingLocked;
  // The shared "Why Decks Unique" sections change every proposal, so only an
  // org admin may edit them — canEditSections is App's isAdmin, i.e.
  // org_members.role === 'admin', the same rule the database enforces
  // (policy ops_write_admins). Non-admins get the plain printed markup.
  const sectionsEditable = inlineEditable && !!props.canEditSections;

  // Org-level wording: the summary labels, the Why Decks Unique and Next
  // steps headings, the Next steps paragraph and the validity days. Same rule
  // as the shared sections — admin only, locked while the draft loads.
  // Editor: live settings, so an edit shows at once. /review: props ARE the
  // copy frozen into the sent proposal, so an old link never changes wording.
  const orgTextEditable = sectionsEditable;
  const proposalText = normalizeProposalText(
    (liveUserSettings as any)?.proposalText
  );
  const proposalTextRef = useRef(proposalText);
  proposalTextRef.current = proposalText;
  const validityDays = resolveValidityDays(proposalText);
  const customNextSteps = resolveNextStepsBody(proposalText);
  // The field being edited keeps its raw text here while it is open: blank
  // is never stored (blank = built-in text), so without this a cleared field
  // would snap straight back to the default under the caret.
  const [orgTextDraft, setOrgTextDraft] = useState<{
    key: keyof ProposalText;
    value: string;
  } | null>(null);
  const [orgTextOpen, setOrgTextOpen] = useState(false);
  const editOrgText = (key: keyof ProposalText, raw: string) => {
    const value =
      key === "validityDays"
        ? cleanDaysInput(raw)
        : key === "nextStepsBody"
          ? raw
          : cleanLabel(raw);
    setOrgTextDraft({ key, value });
    const next: ProposalText = { ...proposalTextRef.current };
    const v = value.trim();
    if (key === "validityDays") {
      if (v) next.validityDays = Number(v);
      else delete next.validityDays;
    } else if (key === "nextStepsBody") {
      // Typed back to the built-in paragraph → the built-in one again, so it
      // keeps following the days.
      if (v && v !== defaultNextStepsBody(resolveValidityDays(next)))
        next.nextStepsBody = value;
      else delete next.nextStepsBody;
    } else {
      const lk = key as ProposalLabelKey;
      if (v && v !== PROPOSAL_LABEL_DEFAULTS[lk]) next[lk] = value;
      else delete next[lk];
    }
    props.onProposalTextChange?.(normalizeProposalText(next));
  };
  const orgTextField = (key: keyof ProposalText, current: string) => ({
    editable: orgTextEditable,
    value: orgTextDraft?.key === key ? orgTextDraft.value : current,
    onChange: (raw: string) => editOrgText(key, raw),
    onOpen: () => setOrgTextOpen(true),
    onClose: () => {
      setOrgTextOpen(false);
      setOrgTextDraft(null);
    },
  });
  const labelField = (key: ProposalLabelKey) => ({
    ...orgTextField(key, resolveLabel(proposalText, key)),
    singleLine: true,
  });

  /* Single source for the printed date. NOTE: this is `new Date()`, i.e.
     TODAY — so a customer opening /review next month sees that day, not the
     date the proposal was issued. Pre-existing behaviour, carried over
     unchanged here; flagged for the Stage 5 data-mapping pass, where the
     validity window also needs a real issue date to count from. */
  const proposalDateLabel = new Date().toLocaleDateString([], {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const autoScopeText = useMemo(() => {
    const txt = sowKey ? sowTemplatesMap?.[sowKey] : "";
    return (txt || "").toString();
  }, [sowKey, sowTemplatesMap]);

  // SOW reads/writes through LIVE props, not the frozen proposalSnapshot —
  // otherwise a controlled textarea sees a stuck value and feels uneditable.
  // Untouched → seed display with the live auto-generated template (and keep
  // tracking construction-type changes). Once touched, bind to the raw stored
  // value with NO fallback, so clearing to empty stays empty and custom text
  // stays custom instead of the default snapping back. Mirrors the ST-124
  // touched-sentinel pattern in ContractPage.
  const liveSowText = props.sowText ?? "";
  const sowTextValue = props.sowTouched
    ? liveSowText
    : liveSowText || autoScopeText;
  const finalScopeText = sowTextValue;
  const handleSowChange = (next: string) => {
    if (typeof props.onSowTextChange === "function") {
      props.onSowTextChange(next);
    }
  };

  // Project Description — same auto-fill-but-editable model as SOW above.
  // Empty stored value → show the generated summary (recomputed live from
  // constructionType + deckingQty); first keystroke saves the user's text and
  // wins from then on. deckingQty is the deck sf on both the editor and the
  // read-only review side (App passes deckingQty={deckingSqFt}; the review
  // row carries deckingQty), so the same generator works in both contexts.
  const autoProjectDescription = useMemo(
    () => buildProjectDescription(constructionType, deckingQty),
    [constructionType, deckingQty]
  );
  const liveProjectDescription = props.projectDescription ?? "";
  const projectDescriptionValue = props.projectDescriptionTouched
    ? liveProjectDescription
    : liveProjectDescription || autoProjectDescription;
  const handleProjectDescriptionChange = (next: string) => {
    if (typeof props.onProjectDescriptionChange === "function") {
      props.onProjectDescriptionChange(next);
    }
  };

  // Rows
  const rows = useMemo(() => {
    // Print ONLY the chosen skirting category, never the combined string.
    // ("Skirting" and "Lattice" are distinct stored values; the table used to
    // hardcode "Skirting / Lattice".) Fall back to the combined label only when
    // the category is genuinely unknown — never assert the wrong single value.
    const skirtCat = (skirtingCategory || "").trim().toLowerCase();
    const skirtingLabel =
      skirtCat === "lattice"
        ? "Lattice"
        : skirtCat === "skirting"
          ? "Skirting"
          : "Skirting / Lattice";

    const baseRows: Array<{
      key: string;
      label: string;
      typeText?: string;
      qtyText: string;
      description: string;
      baseCost: number;
      isFixedPrice?: boolean;
    }> = [
      {
        key: "decking",
        label: "Decking",
        typeText: deckingType || "",
        qtyText: fmtQty(
          deckingQty,
          deckingUnit || "sf",
          deckingType,
          "Decking"
        ),
        description:
          (deckingDescription || "").trim() ||
          "Composite decking installed to match selected finish and layout.",
        baseCost: deckingSubtotal || 0,
      },
      {
        key: "railing",
        label: "Railing",
        typeText: railingType || "",
        qtyText: fmtQty(railingQty, railingUnit || "lf"),
        description:
          (railingDescription || "").trim() ||
          "Railing system installed per code with a durable finish.",
        baseCost: railingSubtotal || 0,
      },
      {
        key: "stairs",
        label: "Stairs",
        typeText: stairsType || "",
        qtyText: fmtQty(stairsQty, stairsUnit || "lf"),
        description:
          (stairsDescription || "").trim() ||
          "Stair system built for safe access with consistent rise/run.",
        baseCost: stairsSubtotal || 0,
      },
      {
        key: "fasteners",
        label: "Fasteners",
        typeText: fastenerType || "",
        qtyText: fmtQty(fastenerQty, fastenerUnit || "ea"),
        description:
          (fastenerDescription || "").trim() ||
          "Fasteners and hardware required for proper installation.",
        baseCost: fastenerSubtotal || 0,
      },
      {
        key: "demo",
        label: "Demolition",
        typeText: demoType || "",
        qtyText: fmtQty(demoQty, demoUnit || "ea"),
        description:
          (demoDescription || "").trim() ||
          "Removal and disposal of existing materials as required.",
        baseCost: demoSubtotal || 0,
      },
      {
        key: "skirting",
        label: skirtingLabel,
        typeText: skirtingType || "",
        qtyText: fmtQty(skirtingQty, skirtingUnit || "sf"),
        description:
          (skirtingDescription || "").trim() ||
          "Finished skirting/lattice to close off underside cleanly.",
        baseCost: skirtingSubtotal || 0,
      },
    ];

    const addOns =
      (addItemsDetailed || [])
        .filter((r) => {
          if ((r as any)?.optional) return false;
          const base = Number(r?.lineBase || 0);
          const hasPicked = !!r?.picked?.name;
          const hasCustomName = !!(r as any)?.customName?.toString().trim();
          const hasCustomDesc = !!(r as any)?.customDescription
            ?.toString()
            .trim();
          return base !== 0 && (hasPicked || hasCustomName || hasCustomDesc);
        })
        .map((r) => {
          const customName = (r as any)?.customName?.toString().trim() || "";
          const customDesc =
            (r as any)?.customDescription?.toString().trim() || "";
          const isMisc = (r.category || "").toLowerCase().trim() === "misc";

          const typeText =
            displayItemName(customName || r.picked?.name || "") ||
            (isMisc ? "Misc Item" : "");

          const baseDesc =
            customDesc ||
            (r.picked as any)?.proposal_description?.toString().trim() ||
            (r.picked as any)?.description?.toString().trim() ||
            (isMisc
              ? ""
              : "Additional feature included as selected in the project scope.");

          const benchType = (r as any)?.benchType || "";
          const benchSuffix =
            benchType.includes("_back")
              ? " Includes backrest."
              : benchType.includes("_storage")
              ? " Includes integrated storage."
              : "";

          const description =
            (r.category || "").toLowerCase().trim() === "bench" && baseDesc
              ? baseDesc + benchSuffix
              : baseDesc;

          return {
            key: `add-${r.rowId}`,
            // Spec §7: never print the internal key. `Rail_options` and
            // `New_Construction` were reaching customers verbatim.
            label:
              displayItemName(r.category) || "Additional Item",
            typeText,
           qtyText: isMisc
  ? (normalizeQtyText((r as any)?.customQtyText) || "—")
  : fmtQty(
      r.qty,
      r.unit,
      customName || r.picked?.name || "",
      r.category || ""
    ),

            description,
            baseCost: Number(r.lineBase || 0),
            isFixedPrice: (r as any).isFixedPrice ?? isMisc,
          };
        }) || [];

    const priced = [...baseRows, ...addOns].filter(
      (r) => (r.baseCost || 0) !== 0
    );

    // Display only — horizontal skirting supersedes the vertical row.
    // Costs are already summed upstream, so the total is unchanged.
    return filterSupersededSkirtingRows(priced, {
      skirtingCategory,
      skirtingType,
    });
  }, [
    addItemsDetailed,
    deckingSubtotal,
    railingSubtotal,
    stairsSubtotal,
    fastenerSubtotal,
    demoSubtotal,
    skirtingSubtotal,
    deckingQty,
    deckingUnit,
    railingQty,
    railingUnit,
    stairsQty,
    stairsUnit,
    fastenerQty,
    fastenerUnit,
    demoQty,
    demoUnit,
    skirtingQty,
    skirtingUnit,
    deckingType,
    railingType,
    stairsType,
    fastenerType,
    demoType,
    skirtingType,
    skirtingCategory,
    deckingDescription,
    railingDescription,
    stairsDescription,
    fastenerDescription,
    demoDescription,
    skirtingDescription,
  ]);

  // ----------------------------------------------------------------
  // Grand total shown on the masthead.
  //
  // Every line prints as money0(baseCost x uplift) and the total used to print
  // as money0(finalEstimate) — each rounded independently off unrounded
  // floats. Over a realistic 6-10 row proposal the printed column can sum to
  // as much as $4 away from the printed total, on a document the customer may
  // well add up.
  //
  // The arithmetic does reconcile exactly: finalEstimate is
  // (sum of upliftable bases) x uplift + (sum of fixed bases), which is
  // precisely the sum of lineTotal() over the rows. So when the rows account
  // for the whole total we can print the sum of the ROUNDED lines and the
  // column adds up.
  //
  // The guard matters. `rows` is a DISPLAY list: filterSupersededSkirtingRows
  // deliberately hides the vertical skirting row when a horizontal skirting
  // add-item supersedes it, and its cost stays in finalEstimate. Summing the
  // visible lines in that case would silently under-quote the job. So we only
  // reconcile when the visible rows are within a dollar of finalEstimate —
  // i.e. nothing priced is hidden — and otherwise fall back to the
  // authoritative figure. Any future display filter inherits that safety.
  // ----------------------------------------------------------------
  const displayTotal = useMemo(() => {
    const authoritative = Number(finalEstimate) || 0;
    const visibleSum = rows.reduce(
      (sum, r) => sum + lineTotal(r.baseCost, (r as any).isFixedPrice),
      0
    );
    if (Math.abs(visibleSum - authoritative) >= 1) return authoritative;
    return rows.reduce(
      (sum, r) =>
        sum + Math.round(lineTotal(r.baseCost, (r as any).isFixedPrice)),
      0
    );
    // lineTotal is a pure closure over upliftMultiplier, which is in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, finalEstimate, upliftMultiplier]);

  // Alternate decking tiers to print, normalized defensively: this prop
  // arrives either from the live estimator or, on the read-only /review page,
  // straight out of a stored proposal payload, where anything could be in the
  // JSON. Non-numeric totals are dropped rather than printed as "$NaN", and
  // the cheapest-first order is re-asserted here so the printed block does not
  // depend on the caller having sorted it.
  const deckingTierRows = useMemo(() => {
    return (Array.isArray(deckingTierOptions) ? deckingTierOptions : [])
      .filter((t) => {
        if (!t || typeof t.level !== "string" || !t.level.trim()) return false;
        // Number(null) and Number("") are BOTH 0 — finite, and they would
        // print a confident "$0" next to a real tier name. A tier that didn't
        // price has to be dropped, so the absence is checked before the
        // coercion that tolerates a JSON round-trip's numeric string.
        const raw = (t as any).total;
        if (raw === null || raw === undefined || raw === "") return false;
        return Number.isFinite(Number(raw));
      })
      .map((t) => ({
        level: t.level.trim(),
        total: Number(t.total),
        diff: Number.isFinite(Number((t as any).diff))
          ? Number((t as any).diff)
          : null,
        isCurrent: !!t.isCurrent,
      }))
      .sort((a, b) => a.total - b.total);
  }, [deckingTierOptions]);

  // The quoted tier heads the table whether or not it was ticked, priced at
  // the proposal's own Total Investment figure — the same number printed at
  // the top of the details page, so the anchor row can never contradict it.
  const quotedTierName = String(
    quotedDeckingTier || deckingTierRows.find((t) => t.isCurrent)?.level || ""
  ).trim();

  // EVERY ticked tier, cheaper ones included, in one strictly ascending run by
  // project total. The quoted tier sits wherever that order puts it rather
  // than being hoisted to the top: the list reads as a price ladder, and a row
  // pinned out of sequence would make the totals column look mis-sorted.
  //
  // The quoted tier is always present, whether or not it was ticked — it is
  // the row the rest of the proposal is written against — and it is priced at
  // the page's own Total Investment figure, never a recomputed one.
  const deckingChoiceRows = useMemo(() => {
    const quotedLc = quotedTierName.toLowerCase();
    const rows = deckingTierRows
      .filter((t) => !(quotedLc && t.level.toLowerCase() === quotedLc))
      .filter((t) => !t.isCurrent)
      .map((t) => ({ ...t, isQuoted: false }));

    if (quotedTierName) {
      rows.push({
        level: quotedTierName,
        total: Number(displayTotal) || 0,
        diff: null,
        isCurrent: true,
        isQuoted: true,
      });
    }

    return rows.sort((a, b) => a.total - b.total);
  }, [deckingTierRows, quotedTierName, displayTotal]);

  // Optional add-items: priced but not part of the main estimate. Mirrors the
  // shape `rows` uses so the same render template works.
  const optionalRows = useMemo(() => {
    return (addItemsDetailed || [])
      .filter((r) => {
        if (!(r as any)?.optional) return false;
        const base = Number(r?.lineBase || 0);
        const hasPicked = !!r?.picked?.name;
        const hasCustomName = !!(r as any)?.customName?.toString().trim();
        const hasCustomDesc = !!(r as any)?.customDescription
          ?.toString()
          .trim();
        return base !== 0 && (hasPicked || hasCustomName || hasCustomDesc);
      })
      .map((r) => {
        const customName = (r as any)?.customName?.toString().trim() || "";
        const customDesc =
          (r as any)?.customDescription?.toString().trim() || "";
        const isMisc = (r.category || "").toLowerCase().trim() === "misc";

        const typeText =
          customName || r.picked?.name || (isMisc ? "Misc Item" : "");

        const baseDesc =
          customDesc ||
          (r.picked as any)?.proposal_description?.toString().trim() ||
          (r.picked as any)?.description?.toString().trim() ||
          (isMisc
            ? ""
            : "Additional feature included as selected in the project scope.");

        const benchType = (r as any)?.benchType || "";
        const benchSuffix =
          benchType.includes("_back")
            ? " Includes backrest."
            : benchType.includes("_storage")
            ? " Includes integrated storage."
            : "";

        const description =
          (r.category || "").toLowerCase().trim() === "bench" && baseDesc
            ? baseDesc + benchSuffix
            : baseDesc;

        return {
          key: `opt-${r.rowId}`,
          // Same §7 rule as the main table — this path printed the raw key
          // ("Decking_options") straight to the customer.
          label: displayItemName(r.category) || "Additional Item",
          typeText,
          qtyText: isMisc
            ? ((r as any)?.customQtyText?.toString().trim() || "—")
            : fmtQty(
                r.qty,
                r.unit,
                customName || r.picked?.name || "",
                r.category || ""
              ),
          description,
          baseCost: Number(r.lineBase || 0),
          isFixedPrice: (r as any).isFixedPrice ?? isMisc,
        };
      });
  }, [addItemsDetailed]);



   const enabledSections = useMemo(() => {
  const raw = (readOnly ? proposalSectionsSnapshot : dbProposalSections) as
    | ProposalSection[]
    | null
    | undefined;

  return Array.isArray(raw) ? raw.filter((s) => s?.enabled) : [];
}, [readOnly, proposalSectionsSnapshot, dbProposalSections]);

  // Visual appendix is sourced from the snapshot (frozen at save time), NOT the
  // DB — so unauthenticated /review customers render the same photos. Map the
  // lightweight {category,label,image_url,caption} into the shape the existing
  // appendix markup expects (id / category / displayName / imageRef / caption).
  const proposalVisuals = useMemo(() => {
    const list = (proposalSnapshot.visuals || []) as ProposalVisual[];
    return list
      .filter((v) => v.image_url?.trim())
      .map((v, i) => ({
        id: `${v.category}-${i}`,
        category: v.category,
        displayName: v.label,
        imageRef: v.image_url,
        caption: v.caption || "",
      }));
  }, [proposalSnapshot.visuals]);

  // Captions shortened to two printed lines, keyed by visual id. Measured
  // against a live caption node so the canvas uses that element's real font;
  // see clampCaptionForPrint for why CSS cannot do this clamping.
  const visualAppendixRef = useRef<HTMLElement | null>(null);
  const [printCaptions, setPrintCaptions] = useState<Record<string, string>>({});

  useLayoutEffect(() => {
    const captioned = proposalVisuals.filter((v) => v.caption.trim());
    if (!captioned.length) {
      setPrintCaptions((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    const probe = visualAppendixRef.current?.querySelector(
      ".proposal-visual-caption-print"
    );
    const measure = makePrintCaptionMeasurer(
      probe ? getComputedStyle(probe).fontFamily : ""
    );
    if (!measure) return;

    const next: Record<string, string> = {};
    for (const v of captioned) next[v.id] = clampCaptionForPrint(v.caption, measure);
    setPrintCaptions((prev) => {
      const unchanged =
        Object.keys(next).length === Object.keys(prev).length &&
        Object.entries(next).every(([k, val]) => prev[k] === val);
      return unchanged ? prev : next;
    });
  }, [proposalVisuals]);


  // ✅ Order from SettingsPage
const layoutOrder = useMemo<string[]>(() => {
  const arr = (liveUserSettings as any)?.proposalLayoutOrder;
  return Array.isArray(arr)
    ? arr
        .map(normalizeLayoutId)
        .map((x) => String(x).trim())
        .filter(Boolean)
    : [];
}, [liveUserSettings]);
layoutOrderRef.current = layoutOrder;

const effectiveOrder = useMemo<string[]>(() => {
  const enabledCustomIds = enabledSections.map((s) => String(s.id).trim());

  // saved order from Settings
  const raw = (layoutOrder || []).map((x) => String(x).trim()).filter(Boolean);

  // keep only enabled custom ids + system ids
  const filtered = raw.filter(
    (id) =>
      id === "__details__" ||
      id === "__timeline__" ||
      enabledCustomIds.includes(id)
  );

  // append any enabled custom sections missing from saved order (DB order)
  const missingCustom = enabledCustomIds.filter((id) => !filtered.includes(id));
  let out = [...filtered, ...missingCustom];

  // ensure system blocks exist exactly once
  if (!out.includes("__details__")) out.push("__details__");
  if (!out.includes("__timeline__")) out.push("__timeline__");

  // de-dupe preserving first occurrence
  out = out.filter((v, i) => out.indexOf(v) === i);

  return out;
}, [layoutOrder, enabledSections]);

  /* Page 1 renders only the marketing blocks, in the Settings order.
     __details__ / __timeline__ are deliberately excluded: the CD design puts
     them on the Project Details page rather than interleaved here. */
  const marketingSections = useMemo(() => {
    return effectiveOrder
      .filter((id) => id !== "__details__" && id !== "__timeline__")
      .map((id) =>
        enabledSections.find((s) => String(s.id).trim() === String(id).trim())
      )
      .filter(Boolean) as ProposalSection[];
  }, [effectiveOrder, enabledSections]);

  /* What the Why Decks Unique list shows ON SCREEN. For an org admin with the
     editor live, that is every section in saved order — hidden (disabled)
     ones included, so they can be found and switched back on. Everyone else,
     and print, get marketingSections: enabled only. Hidden rows are rendered
     .no-print, so they never reach paper; /review never gets here with them
     (readOnly renders enabledSections from the sent copy). */
  const displayedSections = useMemo<ProposalSection[]>(() => {
    if (!sectionsEditable) return marketingSections;
    return orderedSections(
      mergeLayoutOrder(layoutOrder, dbProposalSections),
      dbProposalSections
    );
  }, [sectionsEditable, marketingSections, layoutOrder, dbProposalSections]);

  useEffect(() => {
    dlog("✅ ProposalPage order check", {
      proposalLayoutOrder: (userSettings as any)?.proposalLayoutOrder,
      layoutOrder,
      enabledIds: enabledSections.map((s) => s.id),
      enabledTitles: enabledSections.map((s) => s.title),
      effectiveOrder,
    });
  }, [userSettings, layoutOrder, enabledSections, effectiveOrder]);

  // Header blocks
  /* Identity header, left cell. The reference prints values only — the
     "Name:/Phone:/Email:" run-in labels are not in the approved design. */
  const PreparedBlock = (
    <div className="proposal-headBlock proposal-prepared">
      <div className="proposal-headTitle">Prepared by</div>
      <div className="proposal-headLine">
        {userSettings?.userName || "________________"}
      </div>
      <div className="proposal-headLine">
        {formatPhone(userSettings?.userPhone || "") || "________________"}
      </div>
      <div className="proposal-headLine">
        {userSettings?.userEmail || "________________"}
      </div>
    </div>
  );

  const LogoBlock = (
    <div
      className="proposal-headBlock proposal-logoBlock"
      aria-label="Company logo"
    >
      {userSettings?.logoDataUrl ? (
        <img
          src={userSettings.logoDataUrl}
          alt="Company Logo"
          className="proposal-logoImg"
        />
      ) : (
        <div className="proposal-logoFallback">LOGO</div>
      )}

      {userSettings?.logoSlogan?.trim() ? (
        <div className="proposal-logoSlogan">
          {userSettings.logoSlogan.trim()}
        </div>
      ) : null}
    </div>
  );

  /* Identity header, right cell. The date moved to the title row (spec §4),
     so it is deliberately not repeated here. */
  const ClientBlock = (
    <div className="proposal-headBlock proposal-clientInfo">
      <div className="proposal-headTitle">Prepared for</div>

      <div className="proposal-headLine">
        {[clientFirstName, clientLastName].filter(Boolean).join(" ") ||
          clientLastName ||
          "—"}
      </div>

      <div className="proposal-headLine">
        {[(clientTown || "").trim(), formatStateCode(clientState)]
          .filter(Boolean)
          .join(", ") || "—"}
      </div>

      {/* Spec §7: never print an empty label with nothing after it — an
          absent address reads as a muted "email —", not a bare dash. */}
      <div className="proposal-headLine">
        {clientEmail ? (
          clientEmail
        ) : (
          <span className="du-empty">email —</span>
        )}
      </div>
    </div>
  );

  /* Personal subtitle for the title row: "First Last · Town". Same
     filter(Boolean) discipline as ClientBlock, so a missing first name or an
     absent town collapses cleanly instead of leaving a stray separator, and
     the whole line is dropped when there is nothing to say (spec §7 — never
     print a decorative element with no content behind it). */
  const clientNameLine = [clientFirstName, clientLastName]
    .map((v) => (v || "").trim())
    .filter(Boolean)
    .join(" ");

  const clientTitleLine = [clientNameLine, (clientTown || "").trim()]
    .filter(Boolean)
    .join(" · ");

  /* The details table and the printed timeline sentence now live in the
     page-2 markup below. Only the admin editor survives as a helper — it is
     screen-only chrome, kept out of print by .no-print at the call site. */
  const renderTimelineEditor = () => (
    <div className="proposal-timeline du-timelineEditor">
      <div className="proposal-timeline-field">
        <span className="du-editorLabel">Est. start</span>
        <select
          className="proposal-timeline-select"
          value={startDate ? SPECIFIC_DATE_OPTION : startWeeks}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (v === SPECIFIC_DATE_OPTION) {
              if (!startDate) setStartDate(defaultStartDate(startWeeks));
            } else setStartWeeks(v);
          }}
        >
          <option value={1}>1 week</option>
          <option value={2}>2 weeks</option>
          <option value={3}>3 weeks</option>
          <option value={4}>4 weeks</option>
          <option value={6}>5–6 weeks</option>
          <option value={8}>7–8 weeks</option>
          <option value={SPECIFIC_DATE_OPTION}>Specific date…</option>
        </select>
        {startDate ? (
          <input
            type="date"
            className="proposal-timeline-select"
            aria-label="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        ) : null}
      </div>
      <div className="proposal-timeline-field">
        <span className="du-editorLabel">Est. duration</span>
        <select
          className="proposal-timeline-select"
          value={durationDays}
          onChange={(e) => setDurationDays(Number(e.target.value))}
        >
          <option value={2}>1–2 days</option>
          <option value={5}>3–5 days</option>
          <option value={6}>4–6 days</option>
          <option value={10}>7–10 days</option>
          <option value={14}>10+ days</option>
        </select>
      </div>
    </div>
  );

  return (
    <section
      className={`proposal-page ${readOnly ? "proposal-page--readonly" : ""}`}
      data-theme="light"
    >
      {/* Public review page (readOnly): give the customer a one-tap
          "Save PDF" using the same window.print() path as the editor's
          "Print / Save PDF". no-print keeps the button out of the PDF
          itself; the existing @media print CSS isolates #proposal-doc. */}
      {readOnly && (
        <div className="proposal-actions no-print">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSavePdf}
          >
            Save PDF
          </button>
          <span className="proposal-printHint">{PRINT_MARGINS_HINT}</span>
          {printHint && (
            <div
              role="note"
              style={{
                marginTop: 8,
                maxWidth: 420,
                fontSize: 13,
                lineHeight: 1.4,
                color: "#374151",
                background: "#f3f4f6",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              {printHint}
            </div>
          )}
        </div>
      )}

      {!readOnly && (
        
        <div className="proposal-actions no-print">

  {needsRefresh && (
    <div
      style={{
        marginBottom: 8,
        padding: "8px 12px",
        background: "#fff3f3",
        border: "1px solid #f5c2c2",
        borderRadius: 8,
        color: "#b00020",
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      Changes made to Estimator. Proposal refresh required.
    </div>
  )}

  <button
    type="button"
    className={`btn ${needsRefresh ? "btn-danger" : "btn-secondary"}`}
    onClick={refreshProposal}
    title="Update proposal to latest estimator inputs"
  >
    Refresh Proposal
  </button>

  <button
    type="button"
    className="btn btn-primary"
    onClick={() => window.print()}
  >
            Print / Save PDF
          </button>
          <span className="proposal-printHint">{PRINT_MARGINS_HINT}</span>

          <button
            type="button"
            className={`btn ${
              showLineItemPrices ? "btn-secondary" : "btn-outline"
            }`}
            onClick={() =>
              setShowLineItemPrices((v) => {
                const next = !v;
                try {
                  localStorage.setItem(
                    proposalToggleStorageKey(
                      "du_show_line_prices",
                      ...toggleKeyArgs
                    ),
                    next ? "1" : "0"
                  );
                } catch {}
                return next;
              })
            }
            title="Toggle line item costs"
          >
            {showLineItemPrices ? "Hide Cost" : "Show Cost"}
          </button>

          <button
            type="button"
            className={`btn ${
              showVisualAppendix ? "btn-secondary" : "btn-outline"
            }`}
            onClick={() =>
              setShowVisualAppendix((v) => {
                const next = !v;
                try {
                  localStorage.setItem(
                    proposalToggleStorageKey(
                      "du_show_visual_appendix",
                      ...toggleKeyArgs
                    ),
                    next ? "1" : "0"
                  );
                } catch {}
                return next;
              })
            }
            title="Toggle the Visual Appendix"
          >
            {showVisualAppendix ? "Hide Visuals" : "Show Visuals"}
          </button>
        </div>
      )}

      {!readOnly && (
        <button
          type="button"
className={`btn ${needsRefresh ? "btn-danger" : "btn-secondary"}`}          onClick={async () => {
            dlog("Email button clicked ✅");
            // The send path re-reads the shared sections from the database, so
            // any section edit still saving (clicking this button closes an
            // open editor, which starts its save) must land first — otherwise
            // the customer's /review copy would carry the old text.
            await flushSectionSaves();
            // LIVE props, never the frozen proposalSnapshot: the snapshot only
            // re-syncs on Refresh Proposal or an estimate switch, so its copy of
            // this handler closed over App state from then — change the client
            // email (or phone, notes…) and Email sent the OLD values, to the old
            // address, and saved them into the /review record. Read through
            // latestPropsRef: the await above can span a re-render.
            latestPropsRef.current.onEmailProposal?.();
          }}
        >
          Email Proposal
        </button>
      )}

      {!readOnly && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 10px",
            border: "1px solid rgba(15, 23, 42, 0.12)",
            borderRadius: 8,
            fontSize: 12,
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <strong style={{ fontWeight: 700 }}>Email tracking</strong>

          {!props.proposalId ? (
            <span style={{ opacity: 0.7 }}>
              Not sent yet
            </span>
          ) : (
            <>
              <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: emailOpenedAt ? "#16a34a" : "#e2e8f0",
                  color: emailOpenedAt ? "#fff" : "#475569",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                Opened
              </span>
              <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: emailClickedAt ? "#16a34a" : "#e2e8f0",
                  color: emailClickedAt ? "#fff" : "#475569",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                Clicked
              </span>
            </>
          )}
        </div>
      )}

      {/* Admin note while org-level wording (a label, heading, the days or
          Next steps) is open, and until its save lands (or fails). Screen
          only, fixed to the viewport. */}
      {orgTextEditable &&
      (orgTextOpen ||
        props.proposalTextSaveState === "saving" ||
        props.proposalTextSaveState === "error") ? (
        <div className="no-print du-orgTextNote" role="status">
          <span>
            Editing your standard proposal wording — changes apply to all
            proposals.
          </span>
          <span className="du-sectionsStatus">
            {props.proposalTextSaveState === "saving"
              ? "Saving…"
              : props.proposalTextSaveState === "saved"
                ? "Saved"
                : props.proposalTextSaveState === "error"
                  ? "Not saved — check your connection"
                  : ""}
          </span>
        </div>
      ) : null}

      {/* Off-screen copy of the print footer image. Chrome only has the
          image's inline font ready at print time if it has already rendered
          it; without this the footer text prints blank. */}
      {footerUri ? (
        <img className="du-printFooterPreload" src={footerUri} alt="" aria-hidden="true" />
      ) : null}
      <article ref={docRef as any} className="proposal-doc" id="proposal-doc">
        <div className="du-print-page">
          {userSettings?.logoDataUrl ? (
            <img
              className="du-print-watermark"
              src={userSettings.logoDataUrl}
              alt=""
              aria-hidden="true"
            />
          ) : null}

          <header className="proposal-head">
            <div className="proposal-headSlot proposal-headSlot-left">
              {PreparedBlock}
            </div>
            <div className="proposal-headSlot proposal-headSlot-center">
              {LogoBlock}
            </div>
            <div className="proposal-headSlot proposal-headSlot-right">
              {ClientBlock}
            </div>
          </header>


          {/* --- Title row: page title left, date right, shared baseline (spec §4) --- */}
          <div className="du-titleRow">
            <div className="du-titleBlock">
              <h1 className="du-pageTitle">Project Proposal</h1>
              {/* Client name + town, so the sheet opens by addressing someone
                  rather than a category. Sans against the serif title above it
                  — a subtitle, not a second heading. */}
              {clientTitleLine ? (
                <div className="du-titleFor">{clientTitleLine}</div>
              ) : null}
            </div>
            <div className="du-date">{proposalDateLabel}</div>
          </div>

{!readOnly && (
  <div
    className="no-print"
    style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}
  >
    Last refreshed:{" "}
    {lastRefreshedAt.toLocaleString([], {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })}
  </div>
)}

          {/* --- Project summary: hanging-label grid, no hairlines (spec §4).
               The bodies are edited in place (click → textarea styled like
               the text); there is no separate editor above the document. In
               view mode each renders the same <div className="du-body"> the
               document always printed. --- */}
          <div className="du-sections du-summary">
            <InlineEditText
              className="du-label"
              label="The project heading"
              {...labelField("project")}
            >
              {resolveLabel(proposalText, "project")}
            </InlineEditText>
            <InlineEditText
              className="du-body"
              label="The project"
              placeholder="Describe the project"
              editable={inlineEditable}
              value={projectDescriptionValue}
              onChange={handleProjectDescriptionChange}
            >
              {toParagraphs(projectDescriptionValue).map((para, idx) => (
                <p
                  className="du-tnPara"
                  key={idx}
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {para}
                </p>
              ))}
            </InlineEditText>
            <InlineEditText
              className="du-label"
              label="Scope of work heading"
              {...labelField("scope")}
            >
              {resolveLabel(proposalText, "scope")}
            </InlineEditText>
            <InlineEditText
              className="du-body"
              label="Scope of work"
              placeholder={SOW_FALLBACK_TEXT}
              editable={inlineEditable}
              value={sowTextValue}
              onChange={handleSowChange}
            >
              {toParagraphs(
                finalScopeText?.trim() ? finalScopeText : SOW_FALLBACK_TEXT
              ).map((para, idx) => (
                <p
                  className="du-tnPara"
                  key={idx}
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {para}
                </p>
              ))}
            </InlineEditText>
          </div>

{/* --- Marketing sections: hanging-label grid (spec §4).
     Order still comes from Settings; only the marketing blocks render here.
     __details__ / __timeline__ are no longer interleaved on page 1 — in the
     CD design they belong to the Project Details page, and are rendered
     below. A hairline precedes the first row and follows every row. --- */}
{/* Admin cue: the blocks below are the org's shared template. Screen only. */}
{sectionsEditable ? (
  <div className="no-print du-sectionsBanner" role="status">
    <span>
      Editing your standard proposal sections — changes apply to all
      proposals.
    </span>
    <span className="du-sectionsStatus">
      {sectionSaveState.kind === "saving"
        ? "Saving…"
        : sectionSaveState.kind === "saved"
          ? "Saved"
          : sectionSaveState.kind === "error"
            ? `Not saved — ${sectionSaveState.message}`
            : ""}
    </span>
  </div>
) : null}
{displayedSections.length > 0 ? (
  <>
    {/* If every section is hidden, an admin still sees the heading and the
        hidden rows on screen, but nothing of it prints. */}
    <InlineEditText
      as="h2"
      className={
        marketingSections.length > 0
          ? "du-sectionTitle"
          : "du-sectionTitle no-print"
      }
      label="Why Decks Unique heading"
      {...labelField("whyHeading")}
    >
      {resolveLabel(proposalText, "whyHeading")}
    </InlineEditText>
    {/* No leading .du-hairline: the heading carries the section's 1.5px green
        rule now, and the old grey hairline would sit 14px below it as a
        second rule. Each ROW is still followed by one. */}
    <div
      className={
        marketingSections.length > 0 ? "du-sections" : "du-sections no-print"
      }
    >
      {displayedSections.map((sec, secIdx) => {
        const hidden = !sec.enabled;
        const lines = bulletLines(sec.text);
        // The stored type decides: Bullets → list, anything else → paragraph
        // (line breaks kept). It used to be "Bullets OR the text looks like a
        // list", which silently overrode Paragraph; stored types — the
        // template rows and every sent proposal's frozen copy — were migrated
        // on 2026-09-23 to match what each section printed, so this changes
        // no existing output.
        const asList = lines.length > 0 && sec.type === "bullets";
        return (
          <React.Fragment key={sec.id}>
            {/* .du-row is a BLOCK, not a grid cell: Chromium does not
                fragment grid items, so a long section could not split and
                jumped whole to the next sheet. As a block it splits, and the
                eyebrow is absolutely positioned to keep the hanging-label
                look.

                Title and body go through InlineEditText: for anyone who is
                not an org admin (editable=false) it renders exactly the
                plain element below and nothing else. */}
            <div
              className={hidden ? "du-row du-row--hidden no-print" : "du-row"}
            >
              {hidden ? (
                <span className="du-hiddenBadge">Hidden — not on proposals</span>
              ) : null}
              <InlineEditText
                className="du-label"
                label="Section heading"
                editable={sectionsEditable}
                value={sec.title}
                onChange={(v) => editSectionLocal(sec.id, { title: v })}
                autoOpen={autoOpenSectionId === sec.id}
                selectOnOpen={autoOpenSectionId === sec.id}
                onClose={() => {
                  commitSection(sec.id);
                  if (autoOpenSectionId === sec.id) setAutoOpenSectionId(null);
                }}
              >
                {sec.title}
              </InlineEditText>
              {asList ? (
                <InlineEditText
                  as="ul"
                  className="du-body"
                  label={`${sec.title} text`}
                  editable={sectionsEditable}
                  value={sec.text || ""}
                  onChange={(v) => editSectionLocal(sec.id, { text: v })}
                  onClose={() => commitSection(sec.id)}
                >
                  {lines.map((line, idx) => (
                    <li key={`${sec.id}_${idx}`}>{line}</li>
                  ))}
                </InlineEditText>
              ) : (
                <InlineEditText
                  className="du-body"
                  style={{ whiteSpace: "pre-wrap" }}
                  label={`${sec.title} text`}
                  placeholder="Click to add text"
                  editable={sectionsEditable}
                  value={sec.text || ""}
                  onChange={(v) => editSectionLocal(sec.id, { text: v })}
                  onClose={() => commitSection(sec.id)}
                >
                  {sec.text || ""}
                </InlineEditText>
              )}
              {/* Admin-only reorder / remove, behind one "⋯" button per row.
                  Screen only (.no-print), absolutely positioned in the page
                  margin, so it takes no space on screen or paper. */}
              {sectionsEditable ? (
                <div
                  className={`no-print du-sectionTools${
                    sectionMenuId === sec.id ? " is-open" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="du-sectionTool"
                    aria-label={`Section options for ${sec.title}`}
                    aria-haspopup="menu"
                    aria-expanded={sectionMenuId === sec.id}
                    title="Move or remove this section"
                    onClick={() => {
                      setConfirmRemoveId(null);
                      setSectionMenuId((cur) => (cur === sec.id ? null : sec.id));
                    }}
                  >
                    ⋯
                  </button>
                  {sectionMenuId === sec.id ? (
                    <div className="du-sectionMenu" role="menu">
                      {confirmRemoveId === sec.id ? (
                        <>
                          <div className="du-sectionMenuNote">
                            Remove “{sec.title}” from all proposals?
                          </div>
                          <button
                            type="button"
                            role="menuitem"
                            className="du-sectionMenuItem du-sectionMenuItem--danger"
                            onClick={() => removeSection(sec.id)}
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="du-sectionMenuItem"
                            onClick={() => setConfirmRemoveId(null)}
                          >
                            Keep
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="du-sectionMenuLabel">Format</div>
                          {(["paragraph", "bullets"] as const).map((fmt) => (
                              <button
                                key={fmt}
                                type="button"
                                role="menuitemradio"
                                aria-checked={sec.type === fmt}
                                className={`du-sectionMenuItem du-sectionMenuItem--format${
                                  sec.type === fmt ? " is-current" : ""
                                }`}
                                onClick={() => {
                                  if (sec.type !== fmt) setSectionFormat(sec.id, fmt);
                                }}
                              >
                                {fmt === "paragraph" ? "Paragraph" : "Bullets"}
                              </button>
                            ))}
                          <div className="du-sectionMenuDivider" role="separator" />
                          <button
                            type="button"
                            role="menuitem"
                            className="du-sectionMenuItem"
                            onClick={() => setSectionEnabled(sec.id, hidden)}
                          >
                            {hidden ? "Show on proposals" : "Hide from proposals"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="du-sectionMenuItem"
                            disabled={secIdx === 0}
                            onClick={() => moveSection(sec.id, -1)}
                          >
                            Move up
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="du-sectionMenuItem"
                            disabled={secIdx === displayedSections.length - 1}
                            onClick={() => moveSection(sec.id, 1)}
                          >
                            Move down
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="du-sectionMenuItem du-sectionMenuItem--danger"
                            onClick={() => setConfirmRemoveId(sec.id)}
                          >
                            Remove…
                          </button>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div
              className={hidden ? "du-hairline no-print" : "du-hairline"}
            />
          </React.Fragment>
        );
      })}
    </div>
  </>
) : null}
{sectionsEditable ? (
  <div className="no-print">
    <button type="button" className="du-addSection" onClick={addSection}>
      + Add section
    </button>
  </div>
) : null}

{/* =========================================================
     PAGE 2 — Project Details (spec §5)
     break-before: page is applied in CSS so the price masthead always
     starts a fresh sheet.
     ========================================================= */}
<div className="du-page2">
  {/* Watermark: page 2 only, behind the text (spec §6). */}
  {/* watermarkSrc is the greyscaled bitmap (see the effect above). It
      resolves a tick after mount, so gate on it rather than on logoDataUrl —
      otherwise the first paint would flash the colour original, which is
      exactly what must never reach a printed sheet. */}
  {watermarkSrc ? (
    <div className="du-watermark" aria-hidden="true">
      <img src={watermarkSrc} alt="" />
    </div>
  ) : null}

  {/* ----- Price masthead: no fill, no border, no radius ----- */}
  <div className="du-masthead">
    {/* The one section heading, not .du-pageTitle — that class is reserved
        for "Project Proposal" on page 1. The 1.5px rule for this section is
        on .du-masthead, because the heading shares its line with the total. */}
    <h2 className="du-sectionTitle">Project Details</h2>
    <div className="du-mastheadRight">
      <div className="du-label">Total investment</div>
      {/* No "$" — house rule: no dollar signs anywhere in documents or UI,
          only bare comma-grouped figures. */}
      <div className="du-total">{money0(displayTotal)}</div>
    </div>
  </div>

  {/* Terms line. The deposit half is intentionally omitted for this build —
      there is no deposit field yet; it arrives with the in-place editing
      phase, living on this page rather than in Settings. */}
  <div className="du-terms">
    <div />
    <div>
      {/* Only an editing admin gets the <span>: everyone else keeps the
          plain text node the line always printed. */}
      Valid{" "}
      {orgTextEditable ? (
        <InlineEditText
          as="span"
          className="du-validDays"
          label="Days the proposal is valid"
          placeholder={String(validityDays)}
          inputMode="numeric"
          {...orgTextField("validityDays", String(validityDays))}
          singleLine
        >
          {validityDays}
        </InlineEditText>
      ) : (
        validityDays
      )}{" "}
      days from {proposalDateLabel}
    </div>
  </div>

  {/* ----- Details table ----- */}
  <table className="du-table">
    <thead>
      <tr>
        <th className="du-thItem">Item</th>
        <th className="du-thQty">Qty</th>
        <th>Description</th>
        {showLineItemPrices && <th className="du-thCost">Cost</th>}
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.key}>
          <td>
            <div className="du-itemName">
              {r.label && r.label.toLowerCase() !== "misc"
                ? r.label
                : r.typeText || r.label}
            </div>
            {r.label &&
            r.label.toLowerCase() !== "misc" &&
            r.typeText ? (
              <div className="du-itemSub">{r.typeText}</div>
            ) : null}
          </td>
          <td className="du-qty">{r.qtyText || "—"}</td>
          <td>{r.description}</td>
          {showLineItemPrices && (
            <td className="du-cost">
              {money0(lineTotal(r.baseCost, (r as any).isFixedPrice))}
            </td>
          )}
        </tr>
      ))}
    </tbody>
  </table>

{optionalRows.length > 0 ? (
  /* Same du-table shell, column labels and cells as Project Details, so the
     two tables on the page read as one document. Unlike Project Details the
     Cost column always prints: an add-on is only an offer with its price. */
  <section className="proposal-optional-addons">
    <h2 className="du-sectionTitle">Optional Add-Ons</h2>
    <p className="du-upgradeIntro">
      Enhance your project with any of the options below, available at the
      listed prices.
    </p>
    <table className="du-table">
      <thead>
        <tr>
          <th className="du-thItem">Item</th>
          <th className="du-thQty">Qty</th>
          <th>Description</th>
          <th className="du-thCost">Cost</th>
        </tr>
      </thead>
      <tbody>
        {optionalRows.map((r) => (
          <tr key={r.key}>
            <td>
              <div className="du-itemName">
                {r.label && r.label.toLowerCase() !== "misc"
                  ? r.label
                  : r.typeText || r.label}
              </div>
              {r.label &&
              r.label.toLowerCase() !== "misc" &&
              r.typeText ? (
                <div className="du-itemSub">{r.typeText}</div>
              ) : null}
            </td>
            <td className="du-qty">{r.qtyText || "—"}</td>
            <td>{r.description}</td>
            <td className="du-cost">
              {money0(lineTotal(r.baseCost, (r as any).isFixedPrice))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
) : null}

{/* ----- Decking choices -----
    Every ticked tier in one ascending price ladder, the quoted one sitting in
    its natural position with an AS QUOTED badge. Named "Decking Choices", not
    "Decking Options": "Decking Options" is already a line-item category in the
    Project Details table on this same page, and one label meaning two things
    on one sheet is worse than a longer name.

    Nothing renders at all when no tier is ticked — the section element is the
    conditional, so there is no heading and no reserved space. ----- */}
{deckingTierRows.length > 0 ? (
  <section className="proposal-decking-options">
    <h2 className="du-sectionTitle">Decking Choices</h2>
    <p className="du-upgradeIntro">
      Your proposal is priced in{" "}
      {quotedTierName || deckingType || "the quoted decking"}. The same deck,
      built in any of the following:
    </p>

    <table className="du-table du-upgradeTable">
      <thead>
        <tr>
          <th className="du-thItem">Decking</th>
          <th className="du-thUpgradeDiff">Difference</th>
          <th className="du-thUpgradeTotal">Project Total</th>
        </tr>
      </thead>
      <tbody>
        {deckingChoiceRows.map((t) => {
          // The quoted row IS the proposal's own total, so it has no
          // difference to state. Every other row uses the delta the sidebar
          // panel computed, falling back to arithmetic against the figure
          // printed on this very page.
          const delta =
            t.diff !== null && t.diff !== undefined
              ? t.diff
              : t.total - (Number(displayTotal) || 0);
          const rounded = Math.round(delta);
          return (
            <tr
              key={t.level}
              className={t.isQuoted ? "du-upgradeAnchor" : undefined}
            >
              <td>
                <span className="du-itemName">{t.level}</span>
                {t.isQuoted ? (
                  <span className="du-asQuoted">As quoted</span>
                ) : null}
              </td>
              <td className="du-upgradeDiff">
                {t.isQuoted || rounded === 0
                  ? "—"
                  : `${rounded > 0 ? "+" : "\u2212"} ${money0(
                      Math.abs(rounded)
                    )}`}
              </td>
              <td className="du-upgradeTotal">{money0(t.total)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>

    <p className="du-upgradeFine">
      Pricing valid for {validityDays} days from the proposal date;
      final selection to be confirmed prior to material order.
    </p>
  </section>
) : null}

  {/* ----- Timeline & notes: two equal columns, not cards ----- */}
  {!readOnly ? <div className="no-print">{renderTimelineEditor()}</div> : null}

  <div className="du-tn">
    <div className="du-tnCol">
      <h2 className="du-sectionTitle">Timeline</h2>
      <div className="du-tnBody">
        {startDate
          ? `Estimated start: ${formatStartDate(startDate)}.`
          : `Estimated start: ${formatStartWeeksRange(startWeeks)} from signing.`}{" "}
        Estimated duration: {formatDurationRange(durationDays)}.
      </div>
    </div>
    {/* Spec §7: hide the label entirely when empty rather than printing an
        orphaned heading — the old markup always rendered <h2>Notes</h2>. */}
    {proposalNotes?.trim() || notesAdding || showPermitNote ? (
      <div className="du-tnCol">
        <h2 className="du-sectionTitle">Notes</h2>
        {showPermitNote ? (
          <p className="du-tnBody du-tnPara proposal-permit-note">
            {PERMIT_START_NOTE}
          </p>
        ) : null}
        {/* Keeps .proposal-notes-print: notesLeak.behaviour.test.tsx uses it
            to assert what actually reaches the PDF, and this element still
            wraps all of that text. The class is the guard's hook, not
            decoration.

            The note is split into real paragraphs rather than left to
            white-space:pre-wrap on one element: an author's blank line
            between paragraphs printed as a full empty line, where every other
            block on the sheet uses ordinary paragraph spacing. pre-wrap stays
            per paragraph, so a single newline inside one still breaks. */}
        {proposalNotes?.trim() || notesAdding ? (
        <InlineEditText
          className="du-tnBody proposal-notes-print"
          editorClassName="du-tnBody proposal-notes-input"
          label="Notes"
          placeholder="Notes (optional)"
          editable={inlineEditable}
          value={proposalNotes}
          onChange={(text) => setNotesEntry({ key: NOTES_KEY, text })}
          autoOpen={notesAdding}
          onClose={() => setNotesAdding(false)}
        >
          {notesParagraphs.map((para, idx) => (
            <p
              className="du-tnPara"
              key={idx}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {para}
            </p>
          ))}
        </InlineEditText>
        ) : null}
      </div>
    ) : null}
  </div>

{/* No Notes yet: the section is hidden (it never prints an empty heading), so
    offer a screen-only way in. Opens the same in-place editor. */}
{inlineEditable && !proposalNotes?.trim() && !notesAdding ? (
  <div className="no-print">
    <button
      type="button"
      className="du-addNotes"
      onClick={() => setNotesAdding(true)}
    >
      + Add notes
    </button>
  </div>
) : null}

  {/* ----- Next steps, pinned to the foot of the page ----- */}
  <div className="du-nextSteps">
    {/* No hairline above: the heading's own green rule is this section's
        rule, and a rule on each side of one heading reads as a box. */}
    <InlineEditText
      as="h2"
      className="du-sectionTitle"
      label="Next steps heading"
      {...labelField("nextStepsHeading")}
    >
      {resolveLabel(proposalText, "nextStepsHeading")}
    </InlineEditText>
    {/* The built-in paragraph is still this JSX, so its markup is unchanged;
        an admin's own text prints as typed (line breaks kept). */}
    <InlineEditText
      as="p"
      className="du-nextBody"
      style={customNextSteps ? { whiteSpace: "pre-wrap" } : undefined}
      label="Next steps text"
      placeholder={defaultNextStepsBody(validityDays)}
      {...orgTextField(
        "nextStepsBody",
        customNextSteps ?? defaultNextStepsBody(validityDays)
      )}
    >
      {customNextSteps ?? (
        <>
          This proposal is valid for {numberToWords(validityDays)} (
          {validityDays}) days from the date issued. Pricing and
          material availability are subject to change after this period. If
          you&rsquo;d like to move ahead, we&rsquo;ll prepare a contract covering
          the scope, schedule, and payment terms for your review.
        </>
      )}
    </InlineEditText>
    <div className="du-contactRow">
      <div>
        Questions, or ready to start? Reach out to{" "}
        {userSettings?.userName || "us"}.
      </div>
      <div className="du-contactInfo">
        {[formatPhone(userSettings?.userPhone || ""), userSettings?.userEmail]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </div>
  </div>
</div>

{showVisualAppendix && proposalVisuals.length ? (
  <section className="proposal-visual-appendix proposal-page-break-before" ref={visualAppendixRef}>
    <header className="proposal-visual-header">
      <h2 className="proposal-visual-title">Visual Appendix</h2>
      <p className="proposal-visual-subtitle">
        Photo examples are for visual reference only. Actual colors and styles may vary.
      </p>
    </header>

    <div className="proposal-visual-grid">
      {proposalVisuals.map((record) => (
        <figure key={record.id} className="proposal-visual-card">
          <img src={record.imageRef} alt={record.displayName} className={`proposal-visual-image ${record.category === 'Decking' ? 'contain' : ''}`} />
          {(record.category || '').toLowerCase().includes('skirt') ? (
            <div className="proposal-visual-label">
              <strong>Skirting</strong>
            </div>
          ) : (
            <div className="proposal-visual-label">
              <span className="proposal-visual-category">{(record.category || '').toLowerCase().includes('latt') ? 'Lattice' : displayItemName(record.category)} — </span>
              <strong>{record.displayName}</strong>
            </div>
          )}
          {record.caption?.trim() ? (
            <>
              <div className="proposal-visual-caption">{record.caption}</div>
              <div className="proposal-visual-caption-print" aria-hidden="true">
                {printCaptions[record.id] ?? record.caption}
              </div>
            </>
          ) : null}
        </figure>
      ))}
    </div>
  </section>
) : null}
   </div>
      </article>
    </section>
  );
}